namespace $.$$ {
	export class $shm_agent_app extends $.$shm_agent_app {
		private sessions_source_cache: $shm_agent_session[] = []
		private attachment_draft_db_promise: Promise< $mol_db_database< any > > | null = null
		private attachment_draft_restore_started = new Set< string >()
		private attachment_draft_store_tasks = new Map< string, Promise< void > >()
		private attachment_draft_revisions = new Map< string, number >()
		private pending_create_threads = new Map< string, $shm_agent_thread_data >()
		private pending_create_queue = new Map< string, {
			text: string
			optimistic: string
			attachments: readonly $shm_agent_attachment[]
			mode: 'start' | 'queue' | 'steer'
		}[] >()
		private pending_fork_sources = new Map< string, string >()
		private pending_fork_known_sessions = new Map< string, Set< string > >()
		private pending_fork_threads = new Map< string, $shm_agent_thread_data >()
		private pending_fork_queue = new Map< string, {
			text: string
			optimistic: string
			attachments: readonly $shm_agent_attachment[]
			mode: 'start' | 'queue' | 'steer'
		}[] >()

		@ $mol_mem_key
		session_state( id: string ) {
			$mol_wire_solid()
			return new this.$.$shm_agent_state
		}

		fork_pending( thread = this.session_id() ) {
			return Boolean( thread && ( this.pending_fork_sources.has( thread ) || thread.startsWith( 'pending-fork:' ) ) )
		}

		create_pending( thread = this.session_id() ) {
			return Boolean( thread && ( this.pending_create_threads.has( thread ) || thread.startsWith( 'pending-create:' ) ) )
		}

		pending_create_meta( thread: string ) {
			const match = /^pending-create:(\d+):([^:]*):/.exec( thread )
			if( !match ) return null
			try {
				return { created: Number( match[ 1 ] ), title: decodeURIComponent( match[ 2 ] ) }
			} catch {
				return null
			}
		}

		pending_fork_source_id( thread: string ) {
			const known = this.pending_fork_sources.get( thread )
			if( known ) return known
			return /^pending-fork:([^:]+):/.exec( thread )?.[ 1 ] ?? ''
		}

		agent_sessions_load( refresh = 0 ): { data: $shm_agent_session[] } {
			return $mol_fail( new Error( 'Agent adapter does not implement sessions' ) )
		}

		agent_thread_load( id: string, refresh: number ): $shm_agent_thread_data {
			return $mol_fail( new Error( 'Agent adapter does not implement thread loading' ) )
		}

		agent_session_create(
			title: string,
			cwd: string,
			instructions: string,
			settings: { model?: string, serviceTier?: string | null } = {},
		): { thread: $shm_agent_session } {
			return $mol_fail( new Error( 'Agent adapter does not implement session creation' ) )
		}

		async agent_session_create_async(
			title: string,
			cwd: string,
			instructions: string,
			settings: { model?: string, serviceTier?: string | null } = {},
		): Promise< { thread: $shm_agent_session } > {
			return this.agent_session_create( title, cwd, instructions, settings )
		}

		agent_session_rename( id: string, name: string ): { thread: $shm_agent_session } {
			return $mol_fail( new Error( 'Agent adapter does not implement session rename' ) )
		}

		agent_session_tags( id: string, tags: readonly string[] ): Promise< { thread: $shm_agent_session } > {
			return Promise.reject( new Error( 'Agent adapter does not implement tags' ) )
		}

		agent_session_remove( id: string ): Promise< any > {
			return Promise.reject( new Error( 'Agent adapter does not implement deletion' ) )
		}

		agent_session_fork( id: string ): { thread: $shm_agent_session } {
			return $mol_fail( new Error( 'Agent adapter does not implement forking' ) )
		}

		agent_message_send(
			id: string,
			text: string,
			attachments: readonly $shm_agent_attachment[],
			mode: 'start' | 'queue' | 'steer',
			client_message_id: string,
		): any {
			return $mol_fail( new Error( 'Agent adapter does not implement sending' ) )
		}

		agent_message_force( thread: string, queue: string ): Promise< any > {
			return Promise.reject( new Error( 'Agent adapter does not implement force' ) )
		}

		agent_message_queue_edit( thread: string, queue: string, text: string ): Promise< any > {
			return Promise.reject( new Error( 'Agent adapter does not implement queue editing' ) )
		}

		agent_message_queue_remove( thread: string, queue: string ): Promise< any > {
			return Promise.reject( new Error( 'Agent adapter does not implement queue removal' ) )
		}

		agent_session_stop( id: string ): any {
			return $mol_fail( new Error( 'Agent adapter does not implement stop' ) )
		}

		agent_goal_supported() { return false }
		agent_goal_load( id: string, refresh: number ): any { return null }
		agent_goal_set( id: string, objective: string ): any { return null }
		agent_goal_clear( id: string ): any { return null }
		agent_goal_pause( id: string ): any { return null }
		agent_model_options(): readonly string[] { return [] }
		agent_model_dict(): Record< string, string > { return {} }
		agent_model_id( next?: string ) { return next ?? '' }
		agent_model_default_id( next?: string ) { return next ?? '' }
		agent_model_label() {
			const model = this.agent_model_id()
			return this.agent_model_dict()[ model ] || model
		}
		agent_limits(): readonly { id: string, title: string, value: string }[] { return [] }
		agent_fast_supported() { return false }
		agent_fast_enabled( next?: boolean ) { return next ?? false }
		agent_fast_default_supported() { return false }
		agent_fast_default_enabled( next?: boolean ) { return next ?? false }
		agent_session_defaults(): { model?: string, serviceTier?: string | null } { return {} }
		agent_history_catalog_load( id: string, before: string, refresh: number ): $shm_agent_history_catalog {
			return { items: [], next: null }
		}
		agent_history_turn_load( id: string, turn: string, refresh: number ): $shm_agent_history_turn {
			return { turn_id: turn, user: null, agents: [] }
		}
		agent_event_handle( event: any ) {}
		agent_settings_before_model(): readonly $mol_view[] { return [] }
		agent_settings_after_model(): readonly $mol_view[] { return [] }
		agent_sessions_settings_after_limits(): readonly $mol_view[] { return [] }
		agent_storage_namespace() { return 'agent' }
		agent_name() { return 'Агент' }
		agent_file_text( path: string ) {
			return $mol_fail( new Error( 'Agent adapter does not implement file reading' ) ) as never
		}
		agent_file_data( path: string ) {
			return $mol_fail( new Error( 'Agent adapter does not implement binary file reading' ) ) as never
		}

		server_url( next?: string ): string {
			const host = this.$.$mol_dom_context.location.host
			if( host.endsWith( '.158-160-195-195.ip.hyoo.ru' ) ) return 'https://codex-dev.158-160-195-195.ip.hyoo.ru'
			return 'http://127.0.0.1:4870'
		}

		app_pages() {
			this.events().native()
			this.file_events().native()
			this.pending_create_recover()
			this.pending_fork_recover()
			this.attachment_draft_restore_ensure()
			this.session_bring_ensure()
			this.session_read_ensure()
			return [
				...( this.sessions_page_visible() ? [ this.Sessions_page() ] : [] ),
				this.Chat_page(),
				...( this.history_showed() ? [ this.History_page() ] : [] ),
				...( this.reasoning_message_id() ? [ this.Reasoning_page() ] : [] ),
				...( this.file_path() ? [ this.File_page() ] : [] ),
			]
		}

		@ $mol_mem
		file_path( next?: string ) {
			return this.$.$mol_state_arg.value( 'agent_file', next === undefined ? undefined : next || null ) ?? ''
		}

		@ $mol_mem
		history_showed( next?: boolean ) {
			const value = next === undefined ? undefined : next ? '1' : null
			return this.$.$mol_state_arg.value( 'agent_history', value ) === '1'
		}

		@ $mol_mem_key
		history_refresh( id: string, next?: number ) {
			return next ?? 0
		}

		@ $mol_mem_key
		history_catalog_pages( id: string, next?: number ) {
			return next ?? 1
		}

		@ $mol_mem_key
		history_catalog_page( page: number ): $shm_agent_history_catalog {
			const id = this.session_id()
			if( !id ) return { items: [], next: null }
			const before = page ? this.history_catalog_page( page - 1 ).next || '' : ''
			if( page && !before ) return { items: [], next: null }
			return this.agent_history_catalog_load( id, before, this.history_refresh( id ) )
		}

		@ $mol_mem
		history_catalog_items() {
			const id = this.session_id()
			if( !id ) return []
			const pages = this.history_catalog_pages( id )
			return Array.from( { length: pages }, ( _, page )=> this.history_catalog_page( page ).items ).flat()
		}

		history_has_more() {
			const id = this.session_id()
			if( !id ) return false
			return Boolean( this.history_catalog_page( this.history_catalog_pages( id ) - 1 ).next )
		}

		history_turn_id( next?: string ) {
			if( next !== undefined ) {
				this.$.$mol_state_arg.value( 'agent_history_turn', next || null )
				return next
			}
			const stored = this.$.$mol_state_arg.value( 'agent_history_turn' ) || ''
			return stored || this.history_catalog_items()[ 0 ]?.turn_id || ''
		}

		@ $mol_mem
		history_detail() {
			const id = this.session_id()
			const turn = this.history_turn_id()
			return id && turn ? this.agent_history_turn_load( id, turn, this.history_refresh( id ) ) : null
		}

		@ $mol_action
		history_load_more( next?: Event ) {
			if( next === undefined || !this.history_has_more() ) return null
			const id = this.session_id()
			this.history_catalog_pages( id, this.history_catalog_pages( id ) + 1 )
			return next
		}

		@ $mol_action
		history_open( next?: Event ) {
			if( next === undefined || !this.session_id() ) return null
			const id = this.session_id()
			this.history_catalog_pages( id, 1 )
			this.history_turn_id( '' )
			this.history_refresh( id, this.history_refresh( id ) + 1 )
			this.history_showed( true )
			new this.$.$mol_after_tick( ()=> this.history_bring() )
			return next
		}

		history_bring() {
			const book = this.dom_node() as HTMLElement
			const page = this.History_page().dom_node() as HTMLElement
			book.scroll({
				left: page.offsetLeft + page.offsetWidth - book.offsetWidth,
				behavior: 'smooth',
			} )
		}

		@ $mol_action
		history_close( next?: Event ) {
			if( next === undefined ) return null
			this.history_showed( false )
			return next
		}

		file_path_from_uri( uri: string ) {
			let source = uri
			try {
				source = decodeURIComponent( uri )
			} catch {
				return ''
			}
			if( /^[\\/]?[a-z]:[\\/]/i.test( source ) ) {
				return source.replace( /\\/g, '/' ).replace( /^([a-z]:)/i, '/$1' )
			}
			try {
				const url = new URL( source )
				if( url.protocol === 'file:' ) return decodeURIComponent( url.pathname )
				if( url.protocol !== 'http:' && url.protocol !== 'https:' ) return ''
				const pathname = decodeURIComponent( url.pathname )
				return /^\/[a-z]:\//i.test( pathname ) ? pathname : ''
			} catch {
				return ''
			}
		}

		file_title() {
			const path = this.file_path().replace( /\\/g, '/' )
			return path.slice( path.lastIndexOf( '/' ) + 1 ) || 'Файл'
		}

		file_is_html() {
			return /\.html?(?::\d+(?::\d+)?)?$/i.test( this.file_path() )
		}

		file_is_image() {
			return /\.(?:png|jpe?g|gif|webp)(?::\d+(?::\d+)?)?$/i.test( this.file_path() )
		}

		file_is_video() {
			return /\.(?:webm|mp4|ogv|ogg|mov)(?::\d+(?::\d+)?)?$/i.test( this.file_path() )
		}

		file_content() {
			const path = this.file_path()
			if( this.file_is_html() ) return [ this.File_frame( path ) ]
			if( this.file_is_image() ) return [ this.File_image( path ) ]
			if( this.file_is_video() ) return [ this.File_video( path ) ]
			return [ this.File_code( path ) ]
		}

		file_fullscreen_content() {
			return [ this.file_fullscreen() ? this.File_fullscreen_exit_icon() : this.File_fullscreen_icon() ]
		}

		file_fullscreen_hint() {
			return this.file_fullscreen() ? 'Выйти из полноэкранного режима' : 'На весь экран'
		}

		@ $mol_action
		file_fullscreen_toggle( next?: Event ) {
			if( next === undefined ) return null
			this.file_fullscreen( !this.file_fullscreen() )
			return next
		}

		file_text( source = this.file_path() ) {
			const path = source.replace( /:\d+(?::\d+)?$/, '' )
			return this.agent_file_text( path )
		}

		file_data( source = this.file_path() ) {
			const path = source.replace( /:\d+(?::\d+)?$/, '' )
			return this.agent_file_data( path )
		}

		@ $mol_action
		file_open( path: string ) {
			const local = this.file_path_from_uri( path )
			if( !local ) return ''
			this.file_path( local )
			new this.$.$mol_after_tick( ()=> this.file_bring() )
			return local
		}

		file_bring() {
			const book = this.dom_node() as HTMLElement
			const page = this.File_page().dom_node() as HTMLElement
			page.focus()
			book.scroll({
				left: page.offsetLeft + page.offsetWidth - book.offsetWidth,
				behavior: 'smooth',
			})
		}

		override file_close( next?: Event ) {
			if( next === undefined ) return null
			this.file_fullscreen( false )
			this.file_path( '' )
			return next
		}

		file_keydown( next?: KeyboardEvent ) {
			if( next === undefined || next.key !== 'Escape' || !this.file_path() ) return next ?? null
			next.preventDefault()
			this.file_close( next )
			return next
		}

		chat_tools() {
			return [
				...( this.sessions_pick_visible() ? [ this.Sessions_pick() ] : [] ),
				...( this.session_id() ? [ this.History_toggle() ] : [] ),
				...( this.agent_goal_supported() ? [ this.Goal_settings() ] : [] ),
				...( this.session_id() ? [ this.Intermediate_toggle() ] : [] ),
				this.Chat_settings(),
				...( this.session_id() ? [ this.Fork() ] : [] ),
				this.Chat_provider(),
			]
		}

		sessions_settings_rows() {
			return [
				...( this.agent_limits().length ? [ this.Sessions_limits_label() ] : [] ),
				... this.agent_sessions_settings_after_limits(),
				...( this.agent_model_options().length ? [ this.Sessions_model_label() ] : [] ),
				...( this.agent_fast_default_supported() ? [ this.Sessions_fast() ] : [] ),
			]
		}

		session_limit_rows() {
			return this.agent_limits().map( limit => this.Session_limit( limit.id ) )
		}

		session_limit_title( id: string ) {
			return this.agent_limits().find( limit => limit.id === id )?.title ?? ''
		}

		session_limit_value( id: string ) {
			return this.agent_limits().find( limit => limit.id === id )?.value ?? ''
		}

		sessions_pick_visible() {
			return !this.sessions_page_visible()
		}

		sessions_panel_rows() {
			return [
				...( this.sessions_page_visible() ? [] : [ this.Sessions_head() ] ),
				this.Sessions_scroll(),
			]
		}

		chat_title_content() {
			return [ this.Chat_title() ]
		}

		chat_title_enabled() {
			return Boolean( this.session_id() )
		}

		@ $mol_mem_key
		session_title_draft( id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? this.session_title( id )
		}

		chat_title_draft( next?: string ) {
			const id = this.session_id()
			if( !id ) return 'Чат'
			return this.session_title_draft( id, next )
		}

		@ $mol_action
		override chat_title_commit( next?: Event ) {
			if( next === undefined ) return null
			const id = this.session_id()
			if( !id ) return next
			const previous = this.session_title( id )
			const name = this.chat_title_draft().trim()
			if( !name ) this.chat_title_draft( previous )
			else if( name !== previous ) {
				const result = this.agent_session_rename( id, this.session_create_name( name ) )
				this.session_upsert( result.thread )
				this.chat_title_draft( name )
			}
			return next
		}

		@ $mol_action
		override chat_title_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			const id = this.session_id()
			if( id ) this.chat_title_draft( this.session_title( id ) )
			this.Chat_title().focused( false )
			return next
		}

		chat_body() {
			const id = this.session_id()
			return id ? [ this.Messages_box( this.messages_view_key() ) ] : [ this.No_session() ]
		}

		messages_view_key() {
			return this.session_id() || 'new'
		}

		Messages_current() {
			return this.Messages( this.messages_view_key() )
		}

		@ $mol_mem
		session_brought( next?: string ) {
			return next ?? ''
		}

		@ $mol_action
		session_bring_ensure() {
			const id = this.session_id()
			if( !id || this.session_brought() === id ) return null
			this.session_brought( id )
			new this.$.$mol_after_tick( ()=> {
				if( this.session_id() === id ) this.session_bring()
			} )
			return null
		}

		session_bring() {
			new this.$.$mol_after_tick( ()=> {
				const book = this.dom_node() as HTMLElement
				const page = this.Chat_page().dom_node() as HTMLElement
				book.scroll({
					left: page.offsetLeft + page.offsetWidth - book.offsetWidth,
					behavior: 'smooth',
				})
			} )
		}

		@ $mol_mem
		override session_id( next?: string ) {
			return this.$.$mol_state_arg.value( 'session', next === undefined ? undefined : next || null ) ?? ''
		}

		@ $mol_mem
		reasoning_message_id( next?: string ) {
			return this.$.$mol_state_arg.value( 'reasoning', next === undefined ? undefined : next || null ) ?? ''
		}

		@ $mol_mem_key
		session_thread_refresh( thread: string, next?: number ) {
			return this.session_state( thread ).thread_revision( next )
		}

		thread_refresh( next?: number ) {
			return this.session_thread_refresh( this.session_id() || 'new', next )
		}

		@ $mol_mem_key
		session_goal_refresh( thread: string, next?: number ) {
			return next ?? 0
		}

		@ $mol_mem_key
		session_goal_snapshot( thread: string, next?: {
			generation: number
			data: any
		} ) {
			$mol_wire_solid()
			return next ?? null
		}

		goal_refresh( next?: number ) {
			return this.session_goal_refresh( this.session_id() || 'new', next )
		}

		@ $mol_mem_key
		session_busy_local( thread: string, next?: boolean | null ) {
			const state = this.session_state( thread )
			if( next === undefined ) {
				const phase = state.activity()
				return phase === 'unknown' ? null : state.activity_busy()
			}
			if( next === null ) state.activity_reset()
			else state.activity_confirm( next )
			return next
		}

		@ $mol_mem_key
		session_busy_starting( thread: string, next?: boolean ) {
			const state = this.session_state( thread )
			if( next === undefined ) return state.activity() === 'starting'
			if( next ) state.activity_start()
			else if( state.activity() === 'starting' ) state.activity_reset()
			return next
		}

		busy_local( next?: boolean | null ) {
			return this.session_busy_local( this.session_id(), next )
		}

		session_busy_confirm_active( thread: string ) {
			this.session_state( thread ).activity_confirm( true )
		}

		session_busy_confirm_idle_later( thread: string ) {
			return this.session_busy_confirm_idle_now( thread )
		}

		session_busy_confirm_idle_now( thread: string ) {
			return this.session_state( thread ).activity_confirm( false )
		}

		session_busy_set( thread: string, busy: boolean ) {
			return this.session_state( thread ).activity_confirm( busy )
		}

		@ $mol_mem
		connection_online( next?: boolean ) {
			return next ?? true
		}

		@ $mol_mem_key
		session_activity_error( thread: string, next?: string ) {
			return next ?? ''
		}

		activity_error( next?: string ) {
			return this.session_activity_error( this.session_id(), next )
		}

		@ $mol_mem
		forking( next?: boolean ) {
			return next ?? false
		}

		@ $mol_mem_key
		session_attachments( thread: string, next?: readonly $shm_agent_attachment[] ) {
			const state = this.session_state( thread )
			if( next === undefined ) return state.attachments()
			this.attachment_draft_revisions.set( thread, ( this.attachment_draft_revisions.get( thread ) ?? 0 ) + 1 )
			state.attachments( next )
			this.attachment_draft_store( thread, next )
			return next
		}

		override attachments( next?: readonly $shm_agent_attachment[] ) {
			return this.session_attachments( this.session_id(), next )
		}

		attachment_draft_db() {
			return this.attachment_draft_db_promise ??= this.$.$mol_db< any >( 'shm_agent_drafts',
				migration => migration.store_make( 'attachments' ),
			)
		}

		attachment_draft_key( thread: string ) {
			return `${ this.agent_storage_namespace() }:${ thread || 'new' }`
		}

		attachment_draft_store( thread: string, attachments: readonly $shm_agent_attachment[] ) {
			const key = this.attachment_draft_key( thread )
			const stored = attachments.map( attachment => ({
				... attachment,
				data: attachment.file ? '' : attachment.data,
			}) )
			const previous = this.attachment_draft_store_tasks.get( key ) ?? Promise.resolve()
			const task = previous.catch( $mol_fail_log ).then( async ()=> {
				const database = await this.attachment_draft_db()
				await database.change( 'attachments' ).stores.attachments.put( stored, key )
			} )
			this.attachment_draft_store_tasks.set( key, task )
			task.catch( $mol_fail_log )
		}

		attachment_draft_restore_ensure() {
			const thread = this.session_id()
			if( this.attachment_draft_restore_started.has( thread ) ) return null
			this.attachment_draft_restore_started.add( thread )
			const revision = this.attachment_draft_revisions.get( thread ) ?? 0
			this.attachment_draft_restore( thread, revision ).catch( $mol_fail_log )
			return null
		}

		async attachment_draft_restore( thread: string, revision = this.attachment_draft_revisions.get( thread ) ?? 0 ) {
			const database = await this.attachment_draft_db()
			const stored = await database.read( 'attachments' ).attachments.get( this.attachment_draft_key( thread ) ) as readonly $shm_agent_attachment[] | undefined
			if( ( this.attachment_draft_revisions.get( thread ) ?? 0 ) !== revision ) return false
			if( !stored?.length || this.session_attachments( thread ).length ) return false
			const restored = stored.map( attachment => ({
				... attachment,
				data: attachment.file ? URL.createObjectURL( attachment.file ) : attachment.data,
			}) )
			this.session_attachments( thread, restored )
			return true
		}

		@ $mol_mem
		attachment_drag_depth( next?: number ) {
			return next ?? 0
		}

		@ $mol_mem
		attachment_drag_active( next?: boolean ) {
			return next ?? false
		}

		@ $mol_mem_key
		live_items( thread: string, next?: Record< string, any > ) {
			return this.session_state( thread ).live_items( next )
		}

		thread_poll_stamp() {
			return this.thread_poll_stamp_for( this.session_id() )
		}

		thread_poll_stamp_for( id: string ) {
			if( this.connection_online() ) return 0
			const active = this.session_busy( id )
			const interval = active ? 2_000 : 10_000
			return Math.floor( this.$.$mol_state_time.now( interval ) / interval )
		}

		@ $mol_mem_key
		messages_scroll_stamp( thread: string, next?: string ) {
			return next ?? ''
		}

		@ $mol_mem_key
		messages_stick_bottom( thread: string, next?: boolean ) {
			return next ?? true
		}

		@ $mol_mem_key
		session_external_seen( thread: string, next?: boolean ) {
			return next ?? false
		}

		@ $mol_mem_key
		session_unread_live( thread: string, next?: boolean ) {
			return next ?? false
		}

		session_read_at( thread: string, next?: number ) {
			return Number( this.$.$mol_state_local.value( `shm_${ this.agent_storage_namespace() }_session_read:${ thread }`, next ) ?? 0 )
		}

		@ $mol_mem
		session_read_id( next?: string ) {
			return next ?? ''
		}

		@ $mol_action
		session_read_ensure() {
			const id = this.session_id()
			if( !id || this.session_read_id() === id ) return null
			this.session_read_id( id )
			this.session_unread_live( id, false )
			this.session_read_at( id, Date.now() / 1000 )
			return null
		}

		events(): { native(): any } { return { native: ()=> null } }
		file_events(): { native(): any } { return { native: ()=> null } }

		@ $mol_action
		event_handle( event: any ) {
			if( !this.event_visible( event ) ) return
			this.agent_event_handle( event )
			const thread = event.params?.threadId || event.params?.thread?.id
			if( event.method === 'sessions/snapshot' ) {
				this.sessions_snapshot_apply( event.params?.sessions ?? [] )
				return
			}
			if( thread ) this.session_state( thread ).activity_event( event )
			if( event.method === 'thread/queue/updated' && thread && event.params?.startedMessageId ) {
				this.message_queue_started(
					thread,
					String( event.params.startedMessageId ),
					String( event.params.turnId || '' ),
				)
			}
			const history_changed = [
				'thread/file/updated',
				'thread/queue/updated',
				'turn/completed',
			].includes( event.method ) || ( event.method === 'error' && !event.params?.willRetry )
			if( event.method === 'transport/error' ) {
				this.connection_online( false )
				return
			}
			if( event.method === 'transport/open' ) {
				const reconnected = !this.connection_online()
				this.connection_online( true )
				if( reconnected && ( !thread || thread === this.session_id() ) ) {
					this.thread_refresh( this.thread_refresh() + 1 )
				}
				return
			}
			if( ( event.method === 'thread/started' || event.method === 'thread/forked' ) && event.params?.thread ) {
				const known = this.session_by_id( event.params.thread.id )
				this.session_upsert({
					... event.params.thread,
					...( known?.name ? { name: known.name } : {} ),
				})
			}
			if( event.method === 'thread/name/updated' && thread ) {
				const session = this.session_by_id( thread )
				if( session ) {
					this.session_upsert({ ... session, name: event.params.name })
					this.session_title_draft( thread, this.session_title( thread ) )
				}
			}
			if( event.method === 'thread/tags/updated' && thread ) {
				const session = this.session_by_id( thread )
				if( session ) this.session_upsert({ ... session, tags: event.params.tags ?? [] })
			}
			if( ( event.method === 'thread/archived' || event.method === 'thread/deleted' ) && thread ) this.session_remove( thread )
			if( event.method?.startsWith( 'thread/goal/' ) ) this.goal_refresh( this.goal_refresh() + 1 )
			if(
				thread
				&& thread !== this.session_id()
				&& event.method === 'item/completed'
				&& event.params?.item?.type === 'agentMessage'
			) this.session_unread_live( thread, true )
			if( thread !== this.session_id() ) {
				if( thread && history_changed ) this.session_thread_revalidate( thread )
				return
			}
			if( event.method === 'error' ) {
				const message = String( event.params?.error?.message || `Работа ${ this.agent_name() } прервана` )
				this.activity_error( event.params?.willRetry
					? this.retry_message( event.params, message )
					: message )
			}
			if( event.method === 'thread/queue/error' ) this.activity_error( String( event.params?.message || 'Не удалось запустить сообщение из очереди' ) )
			if( event.method === 'thread/file/updated' ) {
				this.session_external_seen( thread, true )
			}
			if( event.method === 'turn/started' ) {
				this.activity_error( '' )
			}
			if( event.method === 'item/started' && event.params?.item ) {
				this.session_busy_confirm_active( thread )
				this.live_item_set( thread, event.params.turnId, event.params.item )
			}
			if( event.method === 'item/agentMessage/delta' && event.params?.itemId ) {
				this.session_busy_confirm_active( thread )
				const params = event.params
				const items = this.live_items( thread )
				const key = `${ params.turnId }:${ params.itemId }`
				const item = items[ key ] ?? {
					id: key,
					type: 'agentMessage',
					text: '',
					turnStartedAt: Date.now() / 1000,
				}
				this.live_items( thread, {
					... items,
					[ key ]: { ... item, text: `${ item.text ?? '' }${ params.delta ?? '' }` },
				} )
			}
			if( event.method?.startsWith( 'item/reasoning/' ) && event.params?.itemId ) {
				this.session_busy_confirm_active( thread )
				const params = event.params
				const key = `${ params.turnId }:${ params.itemId }`
				const items = this.live_items( thread )
				const item = items[ key ] ?? {
					id: key,
					sourceId: params.itemId,
					type: 'reasoning',
					reasoning: [],
					turnStartedAt: Date.now() / 1000,
				}
				const reasoning = this.message_reasoning_parts( item )
				const delta = String( params.delta ?? params.text ?? '' )
				if( delta ) {
					const last = reasoning[ reasoning.length - 1 ]
					const next = last
						? [ ... reasoning.slice( 0, -1 ), { ... last, text: `${ last.text }${ delta }` } ]
						: [ { type: 'reasoning', title: 'Размышление', text: delta } ]
					this.live_items( thread, { ... items, [ key ]: { ... item, reasoning: next } } )
				}
			}
			if( event.method === 'item/completed' && event.params?.item ) {
				this.live_item_set( thread, event.params.turnId, event.params.item )
			}
			if( history_changed ) {
				this.thread_refresh( this.thread_refresh() + 1 )
			}
			if( event.method === 'turn/completed' ) {
				const prefix = `${ event.params?.turn?.id || event.params?.turnId || '' }:`
				if( prefix.length > 1 ) this.live_items( thread, Object.fromEntries(
					Object.entries( this.live_items( thread ) ).filter( ( [ key, item ] )=>
						!( key.startsWith( prefix ) && ( item as any ).type === 'reasoning' ),
					),
				) )
				if( event.params?.turn?.status === 'failed' ) {
					const message = String( event.params.turn.error?.message || `Ответ ${ this.agent_name() } завершился ошибкой` )
					this.activity_error( event.params?.willRetry ? this.retry_message( event.params, message ) : message )
				}
			}
		}

		retry_message( params: any, message: string ) {
			const retry = Number( params?.retryAttempt ?? 0 )
			const retry_max = Number( params?.retryMax ?? 0 )
			const retry_seconds = Math.max( 1, Math.ceil( Number( params?.retryDelayMs ?? 0 ) / 1000 ) )
			return retry
				? `Модель занята. Повторная попытка ${ retry }/${ retry_max || retry } через ${ retry_seconds } с.`
				: `Соединение прервано, ${ this.agent_name() } повторяет запрос: ${ message }`
		}

		event_visible( event: any ) {
			return true
		}

		live_item_set( thread: string, turn: string, source: any ) {
			if( ![ 'userMessage', 'agentMessage', 'reasoning' ].includes( source.type ) ) return
			const item = { ... source, turnId: turn }
			if( source.type === 'userMessage' ) this.optimistic_message_reconcile( thread, item )
			const key = `${ turn }:${ source.id }`
			const previous = this.live_items( thread )[ key ]
			const reasoning = source.type === 'reasoning'
				? this.message_reasoning_parts( source )
				: source.type === 'agentMessage'
					? Object.entries( this.live_items( thread ) )
						.filter( ( [ id, item ] )=> id.startsWith( `${ turn }:` ) && ( item as any ).type === 'reasoning' )
						.flatMap( ( [ _, item ] )=> this.message_reasoning_parts( item ) )
					: undefined
			this.live_items( thread, {
				... this.live_items( thread ),
				[ key ]: {
					... item,
					sourceId: source.id,
					id: key,
					turnStartedAt: Number( previous?.turnStartedAt ) || Date.now() / 1000,
					...( reasoning ? { reasoning } : {} ),
				},
			} )
		}

		optimistic_message_id() {
			return `optimistic:${ $mol_guid() }`
		}

		optimistic_message_set( thread: string, id: string, text: string, images: readonly string[] = [] ) {
			const current = this.live_items( thread )
			if( current[ id ] ) return id
			const retry = Object.fromEntries( Object.entries( current ).filter( ( [ _, item ] )=>
				!( item.sendError && this.message_value( item ) === text ),
			) )
			this.live_items( thread, {
				... retry,
				[ id ]: {
					id,
					type: 'userMessage',
					content: [{ type: 'input_text', text }],
					images: [ ... images ],
					turnStartedAt: Date.now() / 1000,
					optimistic: true,
				},
			} )
			return id
		}

		optimistic_message_fail( thread: string, id: string, error: unknown ) {
			const current = this.live_items( thread )[ id ]
			if( !current ) return
			this.live_items( thread, {
				... this.live_items( thread ),
				[ id ]: { ... current, sendError: this.send_error_message( error ) },
			} )
		}

		send_error_message( error: unknown ) {
			const message = error instanceof Error ? error.message : String( error )
			if( Number( ( error as any )?.status ) === 409 ) return `Не отправлено: ${ message }`
			return `Не отправлено: ${ message || 'неизвестная ошибка' }`
		}

		optimistic_message_remove( thread: string, id: string ) {
			const items = { ... this.live_items( thread ) }
			delete items[ id ]
			this.live_items( thread, items )
		}

		optimistic_message_images_update( thread: string, id: string, images: readonly string[] ) {
			const current = this.live_items( thread )[ id ]
			if( !current ) return
			this.live_items( thread, {
				... this.live_items( thread ),
				[ id ]: { ... current, images: [ ... images ] },
			} )
		}

		optimistic_message_state( thread: string, id: string, state: string, patch: Record< string, any > = {} ) {
			const current = this.live_items( thread )[ id ]
			if( !current ) return
			this.live_items( thread, {
				... this.live_items( thread ),
				[ id ]: { ... current, deliveryState: state, ... patch },
			})
		}

		optimistic_message_turn_bind( thread: string, id: string, turn: string ) {
			const optimistic = this.live_items( thread )[ id ]
			if( !optimistic || !turn ) return
			this.optimistic_message_state( thread, id, '', { turnId: turn } )
			const persisted = Object.values( this.live_items( thread ) ).find( item =>
				!item.optimistic
					&& item.type === 'userMessage'
					&& this.message_turn_id( item ) === turn
					&& this.message_text_identity( item ) === this.message_text_identity( optimistic )
					&& this.message_images( item ).length === this.message_images( optimistic ).length
			)
			if( persisted ) this.optimistic_message_remove( thread, id )
		}

		optimistic_message_reconcile( thread: string, source: any ) {
			const source_id = String( source?.sourceId || source?.id || '' )
			const client_id = source_id ? `optimistic:${ source_id }` : ''
			if( client_id && this.live_items( thread )[ client_id ] ) {
				this.optimistic_message_reconcile_remove( thread, client_id, source_id )
				return
			}
			const forced_items = Object.entries( this.live_items( thread ) ).filter( ( [ _, item ] )=>
				Boolean( ( item as any ).optimistic && ( item as any ).forcedQueueId ),
			)
			const forced = forced_items.find( ( [ _, item ] )=> ( item as any ).forcedQueueId === source_id )
				?? forced_items.find( ( [ _, item ] )=>
					this.message_text_identity( item ) === this.message_text_identity( source )
						&& this.message_images_identity( item ) === this.message_images_identity( source ),
				)
			if( forced ) {
				this.optimistic_message_reconcile_remove( thread, forced[ 0 ], source_id )
				return
			}
			const turn = this.message_turn_id( source )
			if( !turn ) return
			const pending = Object.entries( this.live_items( thread ) ).find( ( [ _, item ] )=>
				item.optimistic
					&& !item.sendError
					&& item.deliveryMode !== 'queue'
					&& this.message_turn_id( item ).startsWith( 'optimistic:' )
					&& this.message_text_identity( item ) === this.message_text_identity( source )
					&& (
						this.message_images_identity( item ) === this.message_images_identity( source )
						|| this.message_images_reencoded( item, source )
					),
			)
			if( pending ) {
				this.optimistic_message_reconcile_remove( thread, pending[ 0 ], source_id )
				return
			}
			const source_images = this.message_images( source ).length
			const match = Object.entries( this.live_items( thread ) )
				.filter( ( [ _, item ] )=>
					item.optimistic
						&& this.message_turn_id( item ) === turn
						&& this.message_text_identity( item ) === this.message_text_identity( source )
						&& this.message_images( item ).length === source_images,
				)
				[ 0 ]
			if( match ) this.optimistic_message_remove( thread, match[ 0 ] )
		}

		optimistic_message_reconcile_remove( thread: string, id: string, source_id: string ) {
			const items = { ... this.live_items( thread ) }
			const forced_queue = String( items[ id ]?.forcedQueueId || '' )
			delete items[ id ]
			if( forced_queue ) items[ `queue-forced:${ source_id || forced_queue }` ] = {
				id: `queue-forced:${ source_id || forced_queue }`,
				type: 'queueForced',
				forcedQueueId: forced_queue,
			}
			this.live_items( thread, items )
		}

		@ $mol_mem
		sessions_refresh( next?: number ) {
			return next ?? 0
		}

		@ $mol_mem
		sessions_snapshot( next?: $shm_agent_session[] | null ) {
			return next === undefined ? null : next
		}

		@ $mol_mem
		sessions_overlay( next?: Record< string, $shm_agent_session | null > ) {
			return next ?? {}
		}

		session_visible( session: $shm_agent_session ) {
			return true
		}

		@ $mol_mem
		sessions_data( next?: $shm_agent_session[] ) {
			if( next !== undefined ) {
				this.sessions_source_cache = [ ... next ]
				this.sessions_snapshot( next )
				this.sessions_overlay( {} )
				return next
			}
			const source = this.sessions_snapshot() ?? this.$.$mol_error_fence(
				()=> {
					const loaded = this.agent_sessions_load( this.sessions_refresh() ).data
					this.sessions_source_cache = [ ... loaded ]
					return loaded
				},
				error => $mol_fail( error ),
				()=> this.sessions_source_cache,
			)
			const merged = new Map( source.filter( session => this.session_visible( session ) )
				.map( session => [ session.id, session ] ) )
			for( const [ id, session ] of Object.entries( this.sessions_overlay() ) ) {
				if( session === null ) merged.delete( id )
				else if( this.session_visible( session ) ) merged.set( id, {
					... merged.get( id ),
					... session,
				} )
			}
			return [ ... merged.values() ].sort(
				( left, right )=> Number( right.updatedAt ?? right.createdAt ?? 0 )
					- Number( left.updatedAt ?? left.createdAt ?? 0 ),
			)
		}

		@ $mol_action
		sessions_snapshot_apply( sessions: readonly $shm_agent_session[] ) {
			const visible = sessions.filter( session => this.session_visible( session ) )
			const pending = Object.fromEntries( Object.entries( this.sessions_overlay() )
				.filter( ( [ id, session ] )=> session === null || this.create_pending( id ) || this.fork_pending( id ) ) )
			this.sessions_source_cache = [ ... visible ]
			this.sessions_snapshot( [ ... visible ] )
			this.sessions_overlay( pending )
			for( const session of visible ) {
				const type = session.status?.type
				if( type === 'active' ) this.session_state( session.id ).activity_snapshot( true )
				else if( type === 'idle' || type === 'systemError' ) {
					this.session_state( session.id ).activity_snapshot( false )
				}
			}
			return visible
		}

		session_upsert( session: $shm_agent_session ) {
			if( !this.session_visible( session ) ) return session
			const previous = this.sessions_overlay()[ session.id ]
				?? this.sessions_snapshot()?.find( item => item.id === session.id )
				?? this.sessions_source_cache.find( item => item.id === session.id )
			const next = {
				... previous,
				... session,
				name: session.name || previous?.name,
				preview: session.preview || previous?.preview,
			}
			this.sessions_overlay({ ... this.sessions_overlay(), [ session.id ]: next })
			this.pending_create_session_seen( next )
			this.pending_fork_session_seen( next )
			return next
		}

		pending_create_session_seen( session: $shm_agent_session ) {
			const temporary = this.session_id()
			if( !this.create_pending( temporary ) || session.id === temporary ) return
			// The create request is authoritative while it is alive. A snapshot can expose
			// the underlying thread before the adapter has finished registering it.
			if( this.pending_create_threads.has( temporary ) ) return
			const meta = this.pending_create_meta( temporary )
			if( !meta || session.name !== this.session_create_name( meta.title ) ) return
			const stamp = Number( session.createdAt ?? session.updatedAt ?? 0 ) * 1000
			if( stamp && stamp < meta.created - 5_000 ) return
			new this.$.$mol_after_tick( ()=> {
				if( this.session_id() === temporary ) this.pending_create_adopt( temporary, session )
			} )
		}

		@ $mol_action
		pending_create_recover() {
			const temporary = this.session_id()
			if( !this.create_pending( temporary ) ) return null
			if( this.pending_create_threads.has( temporary ) ) return null
			const meta = this.pending_create_meta( temporary )
			if( !meta ) return null
			const target = [ ... this.sessions_data() ]
				.filter( session => session.id !== temporary && session.name === this.session_create_name( meta.title ) )
				.filter( session => Number( session.createdAt ?? session.updatedAt ?? 0 ) * 1000 >= meta.created - 5_000 )
				.sort( ( left, right )=> Number( right.createdAt ?? right.updatedAt ?? 0 ) - Number( left.createdAt ?? left.updatedAt ?? 0 ) )[ 0 ]
			if( target ) new this.$.$mol_after_tick( ()=> {
				if( this.session_id() === temporary ) this.pending_create_adopt( temporary, target )
			} )
			return null
		}

		pending_fork_session_seen( session: $shm_agent_session ) {
			const temporary = this.session_id()
			if( !this.fork_pending( temporary ) || session.id === temporary ) return
			if( this.pending_fork_sources.has( temporary ) ) return
			if( this.pending_fork_known_sessions.get( temporary )?.has( session.id ) ) return
			const source = this.pending_fork_source_id( temporary )
			if( source && session.forkedFromId !== source ) return
			if( !source && !session.forkedFromId ) return
			new this.$.$mol_after_tick( ()=> {
				if( this.session_id() === temporary ) this.pending_fork_adopt( temporary, session )
			} )
		}

		@ $mol_action
		pending_fork_recover() {
			const temporary = this.session_id()
			if( !this.fork_pending( temporary ) ) return null
			if( this.pending_fork_sources.has( temporary ) ) return null
			const source = this.pending_fork_source_id( temporary )
			const created = Number( this.session_by_id( temporary )?.createdAt ?? 0 )
			const now = Date.now() / 1000
			const target = [ ... this.sessions_data() ]
				.filter( session => session.id !== temporary && Boolean( session.forkedFromId ) )
				.filter( session => !this.pending_fork_known_sessions.get( temporary )?.has( session.id ) )
				.filter( session => !source || session.forkedFromId === source )
				.filter( session => {
					const stamp = Number( session.createdAt ?? session.updatedAt ?? 0 )
					return created ? stamp >= created - 5 : stamp >= now - 30 * 60
				} )
				.sort( ( left, right )=> Number( right.createdAt ?? right.updatedAt ?? 0 ) - Number( left.createdAt ?? left.updatedAt ?? 0 ) )[ 0 ]
			if( target ) new this.$.$mol_after_tick( ()=> {
				if( this.session_id() === temporary ) this.pending_fork_adopt( temporary, target )
			} )
			return null
		}

		session_remove( id: string ) {
			this.sessions_overlay({ ... this.sessions_overlay(), [ id ]: null })
			return this.sessions_data()
		}

		session_rows() {
			return this.sessions_filtered().map( session => this.Session( session.id ) )
		}

		session_order_scope() { return this.toString() }

		session_order( next?: readonly string[] ) {
			const value = this.$.$mol_state_local.value<readonly string[]>(
				`shm_${ this.agent_storage_namespace() }_session_order:${ this.server_url() }:${ this.session_order_scope() }`, next,
			)
			return Array.isArray( value ) ? [ ...new Set( value.filter( id => typeof id === 'string' ) ) ] : []
		}

		sessions_drag_locked() { return Boolean( this.search_query().trim() ) }

		session_ids( next?: readonly string[] ): readonly string[] {
			const current = this.sessions_filtered().map( session => session.id )
			if( next === undefined || this.sessions_drag_locked() ) return current
			const visible = new Set( current )
			if( next.length !== current.length || new Set( next ).size !== current.length || next.some( id => !visible.has( id ) ) ) return current
			// Keep hidden/temporarily unloaded sessions' positions in the saved order.
			const previous = this.session_order()
			let index = 0
			const ordered = previous.map( id => visible.has( id ) ? next[ index++ ] : id )
			ordered.push( ...next.slice( index ) )
			this.session_order( ordered )
			return this.sessions_filtered().map( session => session.id )
		}

		search_showed() {
			return this.sessions_data().length >= 10
		}

		sessions_filtered() {
			const order = new Map( this.session_order().map( ( id, index )=> [ id, index ] ) )
			const sessions = [ ... this.sessions_data() ].sort( ( left, right )=>
				Number( this.session_pinned( right.id ) ) - Number( this.session_pinned( left.id ) )
				|| ( order.get( left.id ) ?? -1 ) - ( order.get( right.id ) ?? -1 ) )
			if( !this.search_showed() ) return sessions

			const query = this.search_query().trim().toLowerCase()
			return sessions
				.filter( session => !query
					|| this.session_title( session.id ).toLowerCase().includes( query )
					|| this.session_tags( session.id ).some( tag => tag.toLowerCase().includes( query ) ) )
		}

		session_pinned( id: string, next?: boolean ) {
			return this.$.$mol_state_local.value< boolean >(
				`shm_${ this.agent_storage_namespace() }_session_pinned:${ this.server_url() }:${ id }`, next,
			) ?? false
		}

		sessions_unpin_enabled() {
			return this.sessions_data().some( session => this.session_pinned( session.id ) )
		}

		@ $mol_action
		sessions_unpin_all( event?: Event | null ) {
			if( !event ) return null
			const ids = this.sessions_data().filter( session => this.session_pinned( session.id ) ).map( session => session.id )
			for( const id of ids ) this.session_pinned( id, false )
			return null
		}

		session_archived( id: string, next?: boolean ) {
			return this.$.$mol_state_local.value< boolean >(
				`shm_${ this.agent_storage_namespace() }_session_archived:${ this.server_url() }:${ id }`, next,
			) ?? false
		}

		session_by_id( id: string ) {
			return this.sessions_data().find( session => session.id === id )
		}

		session_title( id: string ) {
			const session = this.session_by_id( id )
			return session?.name || session?.preview || `Сессия ${ id.slice( 0, 8 ) }`
		}

		session_tags( id: string ) {
			return [ ...( this.session_by_id( id )?.tags ?? [] ) ]
		}

		session_tags_label( id: string ) {
			return this.session_tags( id ).map( tag => `#${ tag }` ).join( '  ' )
		}

		all_tags() {
			return [ ... new Set( this.sessions_data().flatMap( session => session.tags ?? [] ) ) ]
				.sort( ( left, right )=> left.localeCompare( right ) )
		}

		@ $mol_mem_key
		session_tag_new_value( id: string, next?: string ) {
			return next ?? ''
		}

		session_tag_title( key: string ) {
			return key.slice( key.indexOf( '\u0000' ) + 1 )
		}

		session_tag_checked( key: string, next?: boolean ) {
			const split = key.indexOf( '\u0000' )
			const id = key.slice( 0, split )
			const tag = key.slice( split + 1 )
			const current = this.session_tags( id )
			if( next === undefined ) return current.includes( tag )
			const tags = next ? [ ... new Set( [ ... current, tag ] ) ] : current.filter( item => item !== tag )
			this.session_tags_submit( id, tags, current )
			return next
		}

		override session_tag_add( id: string, next?: Event ) {
			if( next === undefined ) return null
			const tag = this.session_tag_new_value( id ).trim().replace( /\s+/g, ' ' ).slice( 0, 40 )
			if( !tag ) return next
			const current = this.session_tags( id )
			this.session_tag_new_value( id, '' )
			this.session_tags_submit( id, [ ... new Set( [ ... current, tag ] ) ], current )
			return next
		}

		session_tags_submit( id: string, tags: string[], previous: string[] ) {
			const session = this.session_by_id( id )
			if( !session ) return
			this.session_upsert({ ... session, tags })
			this.session_tags_later( id, tags ).catch( error => {
				const current = this.session_by_id( id )
				if( current ) this.session_upsert({ ... current, tags: previous })
				$mol_fail_log( error )
			} )
		}

		async session_tags_later( id: string, tags: string[] ) {
			const result = await this.agent_session_tags( id, tags )
			this.session_upsert( result.thread )
		}

		override session_delete( id: string, next?: Event ) {
			if( next === undefined ) return null
			this.session_delete_later( id ).catch( $mol_fail_log )
			return next
		}

		async session_delete_later( id: string ) {
			await this.agent_session_remove( id )
			this.session_remove( id )
			if( this.session_id() === id ) this.session_id( this.sessions_data()[ 0 ]?.id ?? '' )
		}

		tag_filter_selected( next?: readonly string[] ) {
			return ( this.$.$mol_state_local.value( `shm_${ this.agent_storage_namespace() }_tag_filter`, next ) as readonly string[] | null ) ?? []
		}

		tag_filter_rows() {
			const tags = this.all_tags()
			return tags.length ? tags.map( tag => this.Tag_filter_check( tag ) ) : [ this.Tags_filter_empty() ]
		}

		tag_filter_title( tag: string ) {
			return tag
		}

		tag_filter_checked( tag: string, next?: boolean ) {
			const current = [ ... this.tag_filter_selected() ]
			if( next === undefined ) return current.includes( tag )
			this.tag_filter_selected( next ? [ ... new Set( [ ... current, tag ] ) ] : current.filter( item => item !== tag ) )
			return next
		}

		session_meta( id: string ) {
			const session = this.session_by_id( id )
			const updated = session?.updatedAt ?? session?.createdAt
			const external = this.session_external_label( id )
			if( !updated ) return `${ session?.cwd ?? '' }${ external ? ` (${ external })` : '' }`
			const date = new Date( updated * 1000 )
			const stamp = date.toLocaleString( [], {
				day: '2-digit',
				month: '2-digit',
				year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			} )
			return `${ stamp }${ external ? ` (${ external })` : '' }`
		}

		session_external( id: string ) {
			const session = this.session_by_id( id )
			if( session?.managedBy === 'shm_projects' ) return false
			const source = session?.source
			return [ 'vscode', 'cli', 'exec', 'mcp' ].includes( source ?? '' )
		}

		session_external_label( id: string ) {
			if( !this.session_external( id ) ) return ''
			const source = this.session_by_id( id )?.source
			if( source === 'vscode' ) return 'VS Code'
			if( source === 'cli' ) return 'CLI'
			if( source === 'mcp' ) return 'MCP'
			return 'внешняя'
		}

		session_busy( id: string ) {
			const summary_active = this.$.$mol_error_fence(
				()=> this.session_by_id( id )?.status?.type === 'active',
				()=> false,
				()=> false,
			)
			return this.session_state( id ).activity_busy( summary_active )
		}

		session_busy_hidden( id: string ) {
			return !this.session_busy( id )
		}

		session_unread( id: string ) {
			if( id === this.session_id() ) return false
			const last = Number( this.session_by_id( id )?.lastAgentAt ?? 0 )
			return this.session_unread_live( id ) || last > this.session_read_at( id )
		}

		session_marker_rows( id: string ) {
			return [
				...( this.session_busy( id ) ? [ this.Session_busy( id ) ] : [] ),
				...( this.session_unread( id ) ? [ this.Session_unread( id ) ] : [] ),
			]
		}

		session_title_rows( id: string ) {
			const markers = this.session_marker_rows( id )
			return [
				...( markers.length ? [ this.Session_markers( id ) ] : [] ),
				this.Session_title( id ),
			]
		}

		session_current_title() {
			const id = this.session_id()
			return id ? this.session_title( id ) : 'Выбрать сессию'
		}

		session_current( id: string ) {
			return id === this.session_id()
		}

		session_arg( id: string ) {
			return { session: id }
		}

		session_uri( id: string ) {
			return this.$.$mol_state_arg.link( this.session_arg( id ) )
		}

		session_thread_revalidate( id: string ) {
			if( !id || this.create_pending( id ) || this.fork_pending( id ) ) return
			this.session_thread_refresh( id, this.session_thread_refresh( id ) + 1 )
		}

		@ $mol_action
		override session_select( id: string, next?: Event ) {
			if( next === undefined ) return null
			const mouse = next as MouseEvent
			if( 'button' in mouse && ( mouse.button || mouse.ctrlKey || mouse.metaKey || mouse.shiftKey || mouse.altKey ) ) return next
			next.preventDefault()
			this.session_thread_revalidate( id )
			if( id === this.session_id() ) return next
			this.session_id( id )
			this.$.$mol_state_arg.go( this.session_arg( id ) )
			return next
		}

		override create( next?: Event ) {
			if( next === undefined ) return null
			const title = this.new_session_title().trim() || this.session_title_next()
			const created = Date.now()
			const temporary = `pending-create:${ created }:${ encodeURIComponent( title ) }:${ $mol_guid() }`
			const optimistic_session: $shm_agent_session = {
				id: temporary,
				name: this.session_create_name( title ),
				createdAt: created / 1000,
				updatedAt: created / 1000,
				status: { type: 'creating' },
			}
			this.pending_create_threads.set( temporary, {
				thread: optimistic_session,
				activeTurnId: null,
				queue: [],
			} )
			this.session_upsert( optimistic_session )
			this.new_session_title( '' )
			const creation = this.create_later( title, temporary )
			this.session_id( temporary )
			this.busy_local( true )
			this.session_bring()
			creation.catch( error => {
				const pending = this.pending_create_queue.get( temporary ) ?? []
				const draft = this.prompt()
				const attachments = [ ... this.attachments(), ... pending.flatMap( item => item.attachments ) ]
				if( this.session_id() === temporary ) this.session_id( '' )
				this.session_remove( temporary )
				this.pending_create_threads.delete( temporary )
				this.pending_create_queue.delete( temporary )
				this.session_busy_local( temporary, false )
				if( !draft.trim() && pending.length ) this.prompt( pending.map( item => item.text ).filter( Boolean ).join( '\n\n' ) )
				else if( draft ) this.prompt( draft )
				this.attachments( attachments )
				$mol_fail_log( error )
			} )
			return next
		}

		async create_later( title = this.new_session_title().trim() || this.session_title_next(), temporary = '' ) {
			return this.session_create_later( title, temporary )
		}

		session_title_next() {
			const used = new Set( this.sessions_data().map( session => this.session_title( session.id ).trim() ) )
			for( let index = 1; ; ++ index ) {
				const title = `Сессия ${ index }`
				if( !used.has( title ) ) return title
			}
		}

		session_create_name( title: string ) {
			return title
		}

		session_create_instructions() {
			return ''
		}

		session_create_cwd() {
			return ''
		}

		async session_create_context_async() {
			return {
				cwd: this.session_create_cwd(),
				instructions: this.session_create_instructions(),
			}
		}

		session_first_title( text: string ) {
			const compact = text.replace( /\s+/g, ' ' ).trim()
			return compact.length > 64 ? `${ compact.slice( 0, 61 ) }...` : compact || 'Новая сессия'
		}

		@ $mol_mem_key
		session_prompt( session: string, next?: string ) {
			const key = `shm_${ this.agent_storage_namespace() }_draft:${ session || 'new' }`
			return this.$.$mol_state_local.value< string >( key, next ) ?? ''
		}

		override prompt( next?: string ) {
			return this.session_prompt( this.session_id(), next )
		}

		session_auto_title_needed( id: string ) {
			const default_title = this.$.$mol_error_fence(
				()=> /^Сессия \d+$/.test( this.session_title( id ) ),
				()=> false,
				()=> false,
			)
			if( !default_title ) return false
			return this.$.$mol_error_fence(
				()=> !( this.thread_data()?.thread.turns ?? [] ).some( ( turn: any )=>
					turn.items.some( ( item: any )=> item.type === 'userMessage' ),
				),
				()=> true,
				()=> true,
			)
		}

		session_auto_title( id: string, text: string ) {
			const title = this.session_first_title( text )
			if( !text.trim() || !/^Сессия \d+$/.test( this.session_title( id ) ) ) return null
			const result = this.agent_session_rename( id, this.session_create_name( title ) )
			this.session_upsert( result.thread )
			return result.thread
		}

		async session_create_later( title: string, temporary = '' ) {
			const context = await this.session_create_context_async()
			const settings = await $mol_wire_async( this ).agent_session_defaults()
			const result = await this.agent_session_create_async(
				this.session_create_name( title ),
				context.cwd,
				context.instructions,
				settings,
			)
			if( temporary ) this.pending_create_adopt( temporary, result.thread )
			else {
				this.new_session_title( '' )
				this.session_id( result.thread.id )
				this.session_upsert( result.thread )
				this.session_bring()
			}
			return result.thread.id
		}

		pending_create_adopt( temporary: string, target_session: $shm_agent_session ) {
			const target = target_session.id
			if( !this.create_pending( temporary ) && this.session_id() !== temporary ) return target
			const pending = [ ...( this.pending_create_queue.get( temporary ) ?? [] ) ]
			const optimistic = this.live_items( temporary )
			const draft = this.session_id() === temporary ? this.prompt() : ''
			const attachments = [ ... this.session_attachments( temporary ) ]
			this.session_remove( temporary )
			this.session_upsert( target_session )
			this.live_items( target, { ... this.live_items( target ), ... optimistic } )
			this.live_items( temporary, {} )
			this.pending_create_threads.delete( temporary )
			this.pending_create_queue.delete( temporary )
			this.session_attachments( temporary, [] )
			if( this.session_id() === temporary ) {
				this.session_id( target )
				if( draft ) this.prompt( draft )
				if( attachments.length ) this.attachments( attachments )
			}
			this.session_busy_local( temporary, false )
			this.session_busy_local( target, Boolean( pending.length ) )
			this.session_bring()
			this.messages_to_bottom_force( target )
			if( pending.length ) {
				const sending = $mol_wire_async( this ).pending_fork_drain( target, pending )
				sending.catch( error => {
					this.session_busy_local( target, false )
					this.session_activity_error( target, this.send_error_message( error ) )
					$mol_fail_log( error )
				} )
				sending.then( ()=> $mol_wire_async( this ).session_auto_title( target, pending[ 0 ].text ) )
					.catch( ()=> null )
			}
			return target
		}

		thread_data() {
			const id = this.session_id()
			if( !id ) return null
			const creating = this.pending_create_threads.get( id )
			if( creating ) return creating
			if( this.create_pending( id ) ) return {
				thread: this.session_by_id( id ) ?? { id, name: this.pending_create_meta( id )?.title ?? 'Новая сессия' },
				activeTurnId: null,
				queue: [],
			}
			const pending = this.pending_fork_threads.get( id )
			if( pending ) return pending
			return this.session_thread_data( id )
		}

		@ $mol_mem_key
		session_thread_data( id: string ) {
			const generation = this.session_thread_refresh( id ) + this.thread_poll_stamp_for( id )
			return this.$.$mol_error_fence(
				()=> this.$.$shm_wire_latest(
					id,
					()=> this.agent_thread_load( id, generation ),
				),
				error => $mol_fail( error ),
				()=> {
					const session = this.$.$mol_error_fence(
						()=> this.session_by_id( id ),
						()=> null,
						()=> null,
					)
					return {
						thread: session ?? { id },
						activeTurnId: null,
						queue: [],
						loading: true,
					}
				},
			)
		}

		chat_title() {
			const id = this.session_id()
			return id ? `${ this.session_title( id ) } (${ this.message_items().length })` : 'Чат'
		}

		chat_provider() {
			const provider = this.agent_model_label() || this.message_thread_data()?.thread.modelProvider || this.agent_name()
			return this.agent_fast_enabled() ? `${ provider } · Fast` : provider
		}

		chat_settings_rows() {
			return [
				... this.agent_settings_before_model(),
				this.Model_label(),
				...( this.agent_fast_supported() ? [ this.Session_fast() ] : [] ),
				... this.agent_settings_after_model(),
			]
		}

		@ $mol_mem
		message_thread_data() {
			return this.message_thread_data_for( this.session_id() )
		}

		@ $mol_mem_key
		message_thread_data_for( id: string ) {
			if( !id ) return null
			if( this.create_pending( id ) || this.fork_pending( id ) ) {
				if( id === this.session_id() ) return this.thread_data()
				return this.pending_create_threads.get( id ) ?? this.pending_fork_threads.get( id ) ?? null
			}
			return this.session_thread_data( id )
		}

		@ $mol_mem
		message_history_items() {
			return this.message_history_items_for( this.session_id() )
		}

		@ $mol_mem_key
		message_history_items_for( id: string ) {
			const thread = this.message_thread_data_for( id )
			return ( thread?.thread.turns ?? [] ).flatMap( ( turn: any )=> turn.items.map( ( item: any )=> ({
				... item,
				sourceId: item.id,
				id: `${ turn.id }:${ item.id }`,
				turnId: turn.id,
				turnStartedAt: turn.startedAt,
			}) ) )
				.filter( ( item: any )=> [ 'userMessage', 'agentMessage', 'systemEvent', 'reasoning' ].includes( item.type ) )
		}

		@ $mol_mem
		message_queue_items() {
			return this.message_queue_items_for( this.session_id() )
		}

		@ $mol_mem_key
		message_queue_items_for( id: string ) {
			const thread = this.message_thread_data_for( id )
			return ( thread?.queue ?? [] ).filter( ( item: any )=> !item.retrying ).map( ( item: any )=> ({
				id: `queue:${ item.id }`,
				type: 'userMessage',
				content: [ { type: 'text', text: item.text } ],
				images: item.images,
				turnStartedAt: item.createdAt,
				queued: true,
				queueId: item.id,
				deliveryMode: 'queue',
			}) )
		}

		@ $mol_mem
		message_items() {
			return this.message_items_for( this.session_id() )
		}

		@ $mol_mem_key
		message_items_for( id: string ) {
			const live_all = Object.values( this.live_items( id ) )
			const removed_queues = new Set( live_all.map( ( item: any )=> item.removedQueueId ).filter( Boolean ) )
			const live = live_all.filter( ( item: any )=> ![ 'reasoning', 'queueRemoved', 'queueForced' ].includes( item.type ) )
			const forcedQueues = new Set( live_all.map( ( item: any )=> item.forcedQueueId ).filter( Boolean ) )
			const history = this.message_history_items_for( id )
			const queued = this.message_queue_items_for( id ).filter( ( item: any )=>
				!forcedQueues.has( item.queueId ) && !removed_queues.has( item.queueId ),
			)
			return this.messages_merge( [ ... history, ... queued ], live )
		}

		override intermediate_hidden( next?: boolean ) {
			return Boolean( this.$.$mol_state_local.value(
				`shm_${ this.agent_storage_namespace() }_intermediate_hidden`,
				next,
			) )
		}

		intermediate_toggle_hint() {
			return this.intermediate_hidden()
				? 'Показать промежуточные ответы'
				: 'Скрыть промежуточные ответы'
		}

		message_turn_id( item: any ) {
			if( item?.turnId ) return String( item.turnId )
			const id = String( item?.id ?? '' )
			if( id.startsWith( 'optimistic:' ) || id.startsWith( 'queue:' ) ) return id
			const separator = id.indexOf( ':' )
			return separator < 0 ? id : id.slice( 0, separator )
		}

		message_items_visible( messages: any[] ) {
			if( !this.intermediate_hidden() ) return messages

			const final_agent = new Map< string, any >()
			for( const item of messages ) {
				if( item.type === 'agentMessage' ) final_agent.set( this.message_turn_id( item ), item )
			}

			return messages.filter( item =>
				item.type === 'userMessage'
					|| ( item.type === 'agentMessage' && final_agent.get( this.message_turn_id( item ) ) === item ),
			)
		}

		messages_merge( history: any[], live: any[] ) {
			const known_ids = new Set( history.map( item => item.id ) )
			const known_source_ids = new Set( history.map( item => this.message_source_id( item ) ).filter( Boolean ) )
			const live_source_ids = new Set< string >()
			const persisted = new Set( history.map( item => this.message_identity( item ) ) )
			const queued = new Set( history.map( item => item.queueId ).filter( Boolean ) )
			const claimed_user_history = new Set< any >()
			return this.messages_chronological([
				... history,
				... live.filter( item => {
					const source = this.message_source_id( item )
					const exact = history.find( saved => saved.id === item.id || (
						source && this.message_source_id( saved ) === source
					) )
					if( exact?.type === 'userMessage' ) claimed_user_history.add( exact )
					if( source && ( known_source_ids.has( source ) || live_source_ids.has( source ) ) ) return false
					if( source ) live_source_ids.add( source )
					return !known_ids.has( item.id )
					&& !( item.queueId && queued.has( item.queueId ) )
					&& !persisted.has( this.message_identity( item ) )
					&& !this.transient_user_message_persisted( item, history, claimed_user_history )
				} ),
			])
		}

		messages_chronological( messages: any[] ) {
			const timed = messages.map( ( item, index )=> ({
				item,
				index,
				time: Number( item?.turnStartedAt ),
			}) )
			if( timed.some( entry => !Number.isFinite( entry.time ) || entry.time <= 0 ) ) return messages
			return timed
				.sort( ( left, right )=> left.time - right.time || left.index - right.index )
				.map( entry => entry.item )
		}

		message_source_id( item: any ) {
			if( item?.sourceId ) return String( item.sourceId )
			const id = String( item?.id ?? '' )
			if( id.startsWith( 'optimistic:' ) || id.startsWith( 'queue:' ) ) return ''
			const separator = id.lastIndexOf( ':' )
			if( separator >= 0 ) return id.slice( separator + 1 )
			return id.startsWith( 'msg_' ) ? id : ''
		}

		transient_user_message_persisted( transient: any, history: any[], claimed = new Set< any >() ) {
			if( transient.type !== 'userMessage' ) return false
			if( transient.deliveryMode === 'queue' || this.message_item_queued( transient ) ) return false
			const accepted = [ ... history ].reverse().find( item =>
				!claimed.has( item )
					&& item.type === 'userMessage'
					&& item.accepted
					&& this.message_text_identity( item ) === this.message_text_identity( transient )
					&& this.message_images_identity( item ) === this.message_images_identity( transient ),
			)
			if( accepted ) {
				claimed.add( accepted )
				return true
			}
			if( transient.sendError ) return false
			// Realtime user items are confirmed too, but rollout may assign a new
			// item id and re-encode the image. Reconcile these representations only
			// within their explicit turn and consume each persisted occurrence once.
			if( !transient.optimistic ) {
				const turn = this.message_turn_id( transient )
				if( !turn || turn.startsWith( 'optimistic:' ) ) return false
				const match = history.find( item =>
					!claimed.has( item ) && item.type === 'userMessage'
						&& this.message_turn_id( item ) === turn
						&& this.message_text_identity( item ) === this.message_text_identity( transient )
						&& ( this.message_images_identity( item ) === this.message_images_identity( transient )
							|| this.message_images_reencoded( item, transient ) ),
				)
				if( match ) claimed.add( match )
				return Boolean( match )
			}
			const started = Number( transient.turnStartedAt ?? 0 )
			if( this.message_turn_id( transient ).startsWith( 'optimistic:' ) && started ) {
				const confirmed = [ ... history ].reverse().find( item =>
					!claimed.has( item )
						&& item.type === 'userMessage'
						&& Number( item.turnStartedAt ?? 0 ) >= started - 1
						&& this.message_text_identity( item ) === this.message_text_identity( transient )
						&& (
							this.message_images_identity( item ) === this.message_images_identity( transient )
							|| this.message_images_reencoded( item, transient )
						),
				)
				if( confirmed ) {
					claimed.add( confirmed )
					return true
				}
			}
			if( transient.deliveryMode === 'steer' ) {
				const match = [ ... history ].reverse().find( item =>
					!claimed.has( item )
						&& item.type === 'userMessage'
						&& Number( item.turnStartedAt ?? 0 ) >= started - 1
						&& this.message_text_identity( item ) === this.message_text_identity( transient )
						&& this.message_images_identity( item ) === this.message_images_identity( transient ),
				)
				if( match ) claimed.add( match )
				return Boolean( match )
			}
			if( transient.forcedQueueId ) {
				const match = [ ... history ].reverse().find( item =>
					!claimed.has( item )
						&& item.type === 'userMessage'
						&& this.message_text_identity( item ) === this.message_text_identity( transient )
						&& this.message_images_identity( item ) === this.message_images_identity( transient )
				)
				if( match ) claimed.add( match )
				return Boolean( match )
			}
			const turn = this.message_turn_id( transient )
			if( !turn || turn.startsWith( 'optimistic:' ) ) return false
			const match = history.find( item =>
				!claimed.has( item )
					&& item.type === 'userMessage'
					&& this.message_turn_id( item ) === turn
					&& this.message_text_identity( item ) === this.message_text_identity( transient )
					&& (
						this.message_images_identity( item ) === this.message_images_identity( transient )
						|| this.message_images_reencoded( item, transient )
					)
			)
			if( match ) claimed.add( match )
			return Boolean( match )
		}

		message_identity( item: any ) {
			const turn = String( item.id ?? '' ).split( ':' )[ 0 ]
			return `${ turn }\u0000${ this.message_payload_identity( item ) }`
		}

		message_rows( thread: string ) {
			const messages = this.message_items_visible( this.message_items_for( thread ) )
			if( thread === this.session_id() ) this.messages_scroll_on_change( thread, messages )
			const ready = messages.filter( item => !this.message_item_queued( item ) )
			const queued = messages.filter( item => this.message_item_queued( item ) )
			const rows = [
				... ready.map( item => this.Message( this.message_view_key( thread, item.id ) ) ),
				...( this.reply_waiting( thread ) ? [ this.Reply_loader( thread ) ] : [] ),
				... queued.map( item => this.Message( this.message_view_key( thread, item.id ) ) ),
			]
			return rows
		}

		message_view_key( thread: string, message: string ) {
			return JSON.stringify([ thread, message ])
		}

		message_view_ref( id: string ) {
			try {
				const parsed = JSON.parse( id )
				if( Array.isArray( parsed ) && parsed.length === 2 ) return {
					thread: String( parsed[ 0 ] ),
					message: String( parsed[ 1 ] ),
				}
			} catch {}
			return { thread: this.session_id(), message: id }
		}

		message_item_queued( item: any ) {
			return Boolean( item?.queued || item?.deliveryState === 'В очереди' )
		}

		reply_waiting( thread = this.session_id() ) {
			return Boolean( thread && thread === this.session_id() && this.session_busy( thread ) )
		}

		reply_elapsed( thread: string ) {
			if( !this.reply_waiting( thread ) ) return ''
			const messages = this.message_items_for( thread )
			const user = [ ... messages ].reverse().find( item => item.type === 'userMessage' )
			const started = Number( user?.turnStartedAt ?? this.$.$mol_state_time.now( 1000 ) / 1000 )
			const elapsed = Math.max( 0, Math.floor( this.$.$mol_state_time.now( 1000 ) / 1000 - started ) )
			return this.duration_format( elapsed )
		}

		duration_format( seconds: number ) {
			const pad = ( value: number )=> String( value ).padStart( 2, '0' )
			const hours = Math.floor( seconds / 3600 )
			const minutes = Math.floor( seconds % 3600 / 60 )
			const rest = seconds % 60
			return hours ? `${ pad( hours ) }:${ pad( minutes ) }:${ pad( rest ) }` : `${ pad( minutes ) }:${ pad( rest ) }`
		}

		@ $mol_mem_key
		message_by_id( id: string ) {
			const ref = this.message_view_ref( id )
			return this.message_items_for( ref.thread ).find( item => item.id === ref.message )
		}

		message_role_key( id: string ) {
			return this.message_by_id( id )?.type ?? ''
		}

		message_embeds_allowed( id: string ) {
			return this.message_by_id( id )?.type !== 'userMessage'
		}

		override message_file_open( uri: string, next?: MouseEvent ) {
			if( next === undefined ) return null
			const file = this.file_path_from_uri( uri )
			if( !file ) return next
			this.file_open( file )
			return next
		}

		message_role( id: string ) {
			const role = this.message_role_key( id )
			if( role === 'userMessage' ) return 'Вы'
			if( role === 'systemEvent' ) return 'Система'
			if( role === 'reasoning' ) return this.agent_name()
			return this.agent_name()
		}

		message_theme( id: string ) {
			return [ 'agentMessage', 'reasoning' ].includes( this.message_role_key( id ) ) ? '$mol_theme_special' : ''
		}

		message_time( id: string ) {
			const time = ( this.message_by_id( id ) as any )?.turnStartedAt
			return time ? new Date( time * 1000 ).toLocaleString() : ''
		}

		message_text( id: string ) {
			const item = this.message_by_id( id )
			return this.message_value( item )
		}

		message_content( id: string ) {
			if( this.message_editing( id ) ) return [
				this.Message_editor( id ),
				...( this.message_images( this.message_by_id( id ) ).length ? [ this.Message_images( id ) ] : [] ),
				this.Message_edit_actions( id ),
			]
			return [
				...( this.message_text( id ) ? [ this.Message_text( id ) ] : [] ),
				...( this.message_images( this.message_by_id( id ) ).length ? [ this.Message_images( id ) ] : [] ),
				...( this.message_videos( this.message_by_id( id ) ).length ? [ this.Message_videos( id ) ] : [] ),
				...( this.message_state( id ) ? [
					this.message_queued( id ) ? this.Message_queue( id ) : this.Message_state( id ),
				] : [] ),
				...( this.message_error( id ) ? [ this.Message_error( id ) ] : [] ),
			]
		}

		Message_reasoning_link( id: string ) {
			return this.message_reasoning_parts( this.message_by_id( id ) ).length
				? super.Message_reasoning_link( id )
				: null as any
		}

		message_reasoning_arg( id: string ) {
			return { ... this.session_arg( this.message_view_ref( id ).thread ), reasoning: id }
		}

		message_reasoning_uri_off( id: string ) {
			return this.$.$mol_state_arg.link( this.session_arg( this.message_view_ref( id ).thread ) )
		}

		message_reasoning_parts( item: any ) {
			if( !item ) return [] as { type: string; title: string; text: string }[]
			const source = item.reasoning ?? item.summary ?? []
			const values = Array.isArray( source ) ? source : [ source ]
			return values.flatMap( ( part: any, index: number )=> {
				if( typeof part === 'string' ) return part.trim()
					? [ { type: 'reasoning', title: `Размышление ${ index + 1 }`, text: part.trim() } ] : []
				const text = String( part?.text ?? part?.summary ?? '' ).trim()
				if( !text ) return []
				return [ {
					type: String( part?.type || 'reasoning' ),
					title: String( part?.title || ( part?.type === 'tool' ? 'Инструмент' : `Размышление ${ index + 1 }` ) ),
					text,
				} ]
			} )
		}

		message_reasoning_title( id: string ) {
			return `Размышления (${ this.message_reasoning_parts( this.message_by_id( id ) ).length })`
		}

		message_reasoning_text( id: string ) {
			return this.message_reasoning_parts( this.message_by_id( id ) )
				.map( part => `**${ part.title }**\n\n${ part.text }` )
				.join( '\n\n' )
		}

		reasoning_item() {
			const id = this.reasoning_message_id()
			return id ? this.message_by_id( id ) : null
		}

		reasoning_status() {
			const item = this.reasoning_item()
			if( !item ) return 'Ответ не найден'
			const time = item.turnStartedAt ? new Date( item.turnStartedAt * 1000 ).toLocaleString() : ''
			const model = item.model || this.thread_data()?.thread.modelProvider || this.agent_name()
			return [ `Ответ ${ this.agent_name() }`, model, time ].filter( Boolean ).join( ' · ' )
		}

		reasoning_rows() {
			return this.message_reasoning_parts( this.reasoning_item() )
				.map( ( _, index )=> this.Reasoning_part( String( index ) ) )
		}

		reasoning_part( id: string ) {
			return this.message_reasoning_parts( this.reasoning_item() )[ Number( id ) ]
		}

		reasoning_part_title( id: string ) {
			return this.reasoning_part( id )?.title ?? ''
		}

		reasoning_part_text( id: string ) {
			return this.reasoning_part( id )?.text ?? ''
		}

		override reasoning_close( next?: Event ) {
			if( next === undefined ) return null
			this.reasoning_message_id( '' )
			return next
		}

		message_queued( id: string ) {
			return this.message_item_queued( this.message_by_id( id ) )
		}

		message_queue_id( id: string ) {
			const item = this.message_by_id( id )
			if( item?.queueId ) return String( item.queueId )
			const message = this.message_view_ref( id ).message
			return item?.queued && message.startsWith( 'queue:' ) ? message.slice( 'queue:'.length ) : ''
		}

		message_force_enabled( id: string ) {
			return Boolean( this.message_queue_id( id ) )
		}

		message_edit_enabled( id: string ) {
			return Boolean( this.message_queue_id( id ) )
		}

		override message_edit( id: string, next?: Event ) {
			if( next === undefined || !this.message_edit_enabled( id ) ) return null
			this.message_edit_draft( id, this.message_text( id ) )
			this.message_editing( id, true )
			return next
		}

		message_edit_save_enabled( id: string ) {
			return Boolean( this.message_edit_draft( id ).trim() || this.message_images( this.message_by_id( id ) ).length )
		}

		override message_edit_save( id: string, next?: Event ) {
			if( next === undefined || !this.message_edit_save_enabled( id ) ) return null
			$mol_wire_async( this ).message_edit_save_later( id ).catch( error => {
				this.activity_error( this.send_error_message( error ) )
				$mol_fail_log( error )
			} )
			return next
		}

		async message_edit_save_later( id: string ) {
			const thread = this.message_view_ref( id ).thread
			const queue = this.message_queue_id( id )
			if( !thread || !queue ) return false
			const text = this.message_edit_draft( id ).trim()
			const result = await this.agent_message_queue_edit( thread, queue, text )
			this.message_queue_edited( thread, queue, String( result?.message?.text ?? text ) )
			this.message_editing( id, false )
			this.session_thread_refresh( thread, this.session_thread_refresh( thread ) + 1 )
			return true
		}

		override message_edit_cancel( id: string, next?: Event ) {
			if( next === undefined ) return null
			this.message_editing( id, false )
			return next
		}

		message_queue_started( thread: string, queue: string, turn: string ) {
			const items = this.live_items( thread )
			let changed = false
			const next = Object.fromEntries( Object.entries( items ).map( ( [ key, item ] )=> {
				if( ( item as any ).queueId !== queue ) return [ key, item ]
				changed = true
				return [ key, {
					... item,
					queued: false,
					queueId: '',
					deliveryMode: 'start',
					deliveryState: '',
					...( turn ? { turnId: turn } : {} ),
				} ]
			} ) )
			if( changed ) this.live_items( thread, next )
			return changed
		}

		message_queue_edited( thread: string, queue: string, text: string ) {
			const items = this.live_items( thread )
			let changed = false
			const next = Object.fromEntries( Object.entries( items ).map( ( [ key, item ] )=> {
				if( ( item as any ).queueId !== queue ) return [ key, item ]
				changed = true
				return [ key, { ...item, content: [{ type: 'input_text', text }] } ]
			} ) )
			if( changed ) this.live_items( thread, next )
			return changed
		}

		override message_force( id: string, next?: Event ) {
			if( next === undefined ) return null
			const operation = this.message_force_optimistic( id )
			if( operation ) this.message_force_later( operation ).catch( $mol_fail_log )
			return next
		}

		message_force_optimistic( id: string ) {
			const thread = this.message_view_ref( id ).thread
			const queue = this.message_queue_id( id )
			if( !thread || !queue ) return
			const source = this.message_by_id( id )
			const before = this.live_items( thread )
			const current = Object.fromEntries( Object.entries( before )
				.filter( ( [ _, item ] )=> ( item as any ).queueId !== queue ) )
			const optimistic = source?.optimistic ? source.id : `optimistic:${ queue }`
			this.live_items( thread, {
				... current,
				[ optimistic ]: {
					... source,
					id: optimistic,
					optimistic: true,
					queued: false,
					queueId: '',
					forcedQueueId: queue,
					deliveryMode: 'steer',
					deliveryState: 'Направлено в текущий ответ',
					turnStartedAt: Date.now() / 1000,
				},
			} )
			return { thread, queue, before }
		}

		async message_force_later( operation: { thread: string, queue: string, before: Record< string, any > } ) {
			const { thread, queue, before } = operation
			try {
				await this.agent_message_force( thread, queue )
				this.thread_refresh( this.thread_refresh() + 1 )
			} catch( error ) {
				this.live_items( thread, before )
				this.thread_refresh( this.thread_refresh() + 1 )
				throw error
			}
		}

		message_remove_enabled( id: string ) {
			return Boolean( this.message_queue_id( id ) )
		}

		override message_remove( id: string, next?: Event ) {
			if( next === undefined ) return null
			const operation = this.message_remove_optimistic( id )
			if( operation ) this.message_remove_later( operation ).catch( $mol_fail_log )
			return next
		}

		message_remove_optimistic( id: string ) {
			const thread = this.message_view_ref( id ).thread
			const queue = this.message_queue_id( id )
			if( !thread || !queue ) return
			const before = this.live_items( thread )
			const current = Object.fromEntries( Object.entries( before )
				.filter( ( [ _, item ] )=> ( item as any ).queueId !== queue ) )
			const marker = `queue-removed:${ queue }`
			this.live_items( thread, {
				... current,
				[ marker ]: { id: marker, type: 'queueRemoved', removedQueueId: queue },
			} )
			return { thread, queue, before }
		}

		async message_remove_later( operation: { thread: string, queue: string, before: Record< string, any > } ) {
			const { thread, queue, before } = operation
			try {
				await this.agent_message_queue_remove( thread, queue )
				this.session_thread_refresh( thread, this.session_thread_refresh( thread ) + 1 )
			} catch( error ) {
				this.live_items( thread, before )
				this.session_activity_error( thread, this.send_error_message( error ) )
				this.session_thread_refresh( thread, this.session_thread_refresh( thread ) + 1 )
				throw error
			}
		}

		message_state( id: string ) {
			const item = this.message_by_id( id )
			if( item?.queued ) return 'В очереди'
			return item?.deliveryState ?? ''
		}

		message_error( id: string ) {
			return this.message_by_id( id )?.sendError ?? ''
		}

		message_images( item: any ) {
			if( !item ) return [] as string[]
			const direct = Array.isArray( item.images ) ? item.images : []
			const content = Array.isArray( item.content ) ? item.content : []
			return [
				... direct,
				... content.map( ( part: any )=> part.image_url || part.imageUrl || part.url || '' ).filter( Boolean ),
				... this.message_link_images( this.message_value( item ) ),
			].filter( ( image, index, all )=> all.indexOf( image ) === index )
		}

		message_link_images( text: string ) {
			const images = [] as string[]
			const links = text.matchAll( /!?\[[^\]]*\]\(\s*(?:<([^>]+)>|([^)]+?))\s*\)/g )
			for( const match of links ) {
				const uri = String( match[ 1 ] || match[ 2 ] || '' )
					.trim()
					.replace( /\s+["'][^"']*["']\s*$/, '' )
				if( /^(?:data:image\/(?:png|jpeg|gif|webp);|.*\.(?:png|jpe?g|gif|webp)(?:[?#].*)?$)/i.test( uri ) ) images.push( uri )
			}
			return images
		}

		message_videos( item: any ) {
			if( !item ) return [] as string[]
			const videos = [] as string[]
			const links = this.message_value( item ).matchAll( /!?\[[^\]]*\]\(\s*(?:<([^>]+)>|([^)]+?))\s*\)/g )
			for( const match of links ) {
				const uri = String( match[ 1 ] || match[ 2 ] || '' )
					.trim()
					.replace( /\s+["'][^"']*["']\s*$/, '' )
				if( /\.(?:webm|mp4|ogv|ogg|mov)(?:[?#].*)?$/i.test( uri ) ) videos.push( uri )
			}
			return videos.filter( ( video, index, all )=> all.indexOf( video ) === index )
		}

		message_image_rows( id: string ) {
			return this.message_images( this.message_by_id( id ) ).map( ( _, index )=> this.Message_image( `${ id }\u0000${ index }` ) )
		}

		message_image_uri( key: string ) {
			const split = key.lastIndexOf( '\u0000' )
			const id = key.slice( 0, split )
			const index = Number( key.slice( split + 1 ) )
			return this.message_image_uri_resolve( this.message_images( this.message_by_id( id ) )[ index ] ?? '' )
		}

		message_image_uri_resolve( uri: string ) {
			return uri
		}

		message_video_rows( id: string ) {
			return this.message_videos( this.message_by_id( id ) ).map( ( _, index )=> this.Message_video( `${ id }\u0000${ index }` ) )
		}

		message_video_uri( key: string ) {
			const split = key.lastIndexOf( '\u0000' )
			const id = key.slice( 0, split )
			const index = Number( key.slice( split + 1 ) )
			return this.message_video_uri_resolve( this.message_videos( this.message_by_id( id ) )[ index ] ?? '' )
		}

		message_video_uri_resolve( uri: string ) {
			return uri
		}

		message_value( item: any ): string {
			if( !item ) return ''
			if( item.type === 'userMessage' ) return item.content?.map( ( part: any )=> part.text || '' ).join( '\n' ) ?? ''
			if( item.type === 'agentMessage' ) return item.text ?? ''
			if( item.type === 'systemEvent' ) return item.text ?? ''
			if( item.type === 'reasoning' ) return ''
			return ''
		}

		message_text_identity( item: any ) {
			return this.message_value( item )
				.replace( /\r\n?/g, '\n' )
				.split( '\n' ).map( line => line.trimEnd() ).join( '\n' )
				.trim()
		}

		message_images_identity( item: any ) {
			return this.message_images( item )
				.map( image => `${ image.length }:${ image.slice( -64 ) }` )
				.join( '|' )
		}

		message_images_reencoded( left: any, right: any ) {
			const left_images = this.message_images( left )
			const right_images = this.message_images( right )
			return Boolean( left_images.length && left_images.length === right_images.length )
		}

		message_payload_identity( item: any ) {
			const images = this.message_images_identity( item )
			return `${ item?.type ?? '' }\u0000${ this.message_value( item ) }\u0000${ images }`
		}

		messages_stamp( messages: any[] ) {
			const last = messages[ messages.length - 1 ]
			const reasoning = this.message_reasoning_parts( last )
				.map( part => part.text.length )
				.join( ',' )
			const state = last?.queued ? 'queued' : String( last?.deliveryState ?? last?.sendError ?? '' )
			return `${ messages.length }:${ last?.type ?? '' }:${ this.message_value( last ).length }:${ this.message_images_identity( last ) }:${ reasoning }:${ state }`
		}

		messages_scroll_on_change( thread: string, messages: any[] ) {
			if( !thread ) return
			const stamp = this.messages_stamp( messages )
			const previous = this.messages_scroll_stamp( thread )
			if( stamp === previous ) return
			const should_scroll = !previous || this.messages_stick_bottom( thread )
			this.messages_scroll_stamp( thread, stamp )
			if( should_scroll ) new this.$.$mol_after_frame( ()=> this.messages_to_bottom() )
		}

		messages_near_bottom() {
			const node = this.Messages_current().dom_node()
			return node.scrollHeight - node.scrollTop - node.clientHeight < 32
		}

		override messages_scroll( next?: Event ) {
			if( next === undefined ) return null
			const thread = this.session_id()
			if( thread ) this.messages_stick_bottom( thread, this.messages_near_bottom() )
			return next
		}

		messages_to_bottom() {
			const node = this.Messages_current().dom_node()
			const thread = this.session_id()
			const scroll = ()=> {
				if( thread === this.session_id() && this.messages_stick_bottom( thread ) ) node.scrollTop = node.scrollHeight
			}
			scroll()
			new this.$.$mol_after_frame( scroll )
			new this.$.$mol_after_frame( ()=> new this.$.$mol_after_frame( scroll ) )
			new this.$.$mol_after_timeout( 100, scroll )
		}

		messages_to_bottom_force( thread = this.session_id() ) {
			if( !thread ) return
			this.messages_stick_bottom( thread, true )
			const scroll = ()=> {
				if( thread !== this.session_id() ) return
				this.messages_stick_bottom( thread, true )
				const node = this.Messages_current().dom_node()
				node.scrollTop = node.scrollHeight
			}
			new this.$.$mol_after_frame( scroll )
			new this.$.$mol_after_frame( ()=> new this.$.$mol_after_frame( scroll ) )
			new this.$.$mol_after_timeout( 100, scroll )
		}

		@ $mol_mem
		goal_data() {
			const id = this.session_id()
			if( !id || this.create_pending( id ) || this.fork_pending( id ) ) return null
			const generation = this.session_goal_refresh( id )
			const snapshot = this.session_goal_snapshot( id )
			return this.$.$shm_wire_latest(
				id,
				()=> snapshot?.generation === generation
					? snapshot.data
					: this.$.$mol_error_fence(
						()=> this.agent_goal_load( id, generation ),
						()=> null,
						()=> snapshot?.data ?? null,
					),
			)
		}

		goal_objective() {
			return this.goal_data()?.objective ?? ''
		}

		goal_state() {
			return this.goal_data()?.status ?? ''
		}

		goal_state_title() {
			switch( this.goal_state() ) {
				case 'active': return 'Активна'
				case 'complete':
				case 'completed': return 'Завершена'
				case 'blocked': return 'Заблокирована'
				case 'paused': return 'Приостановлена'
				default: return this.goal_state() || 'Не задана'
			}
		}

		goal_hint() {
			const objective = this.goal_objective()
			return objective ? `Цель: ${ objective }\nСтатус: ${ this.goal_state_title() }` : 'Цель не задана'
		}

		@ $mol_mem_key
		goal_draft( id: string, next?: string ) {
			return next ?? this.goal_data()?.objective ?? ''
		}

		override goal_text( next?: string ) {
			return this.goal_draft( this.session_id(), next )
		}

		goal_status() {
			const goal = this.goal_data()
			if( !goal ) return 'Не задана'
			return `${ this.goal_state_title() } · ${ goal.tokensUsed } токенов · ${ Math.round( goal.timeUsedSeconds ) } с`
		}

		goal_clear_enabled() {
			return Boolean( this.goal_data() )
		}

		goal_clear_title() {
			return this.goal_state() === 'active' ? 'Остановить цель' : 'Очистить цель'
		}

		override goal_set( next?: Event ) {
			if( next === undefined ) return null
			const objective = this.goal_text().trim()
			if( !objective ) return next
			$mol_wire_async( this ).goal_set_later( objective ).catch( $mol_fail_log )
			return next
		}

		goal_set_later( objective: string ) {
			this.agent_goal_set( this.session_id(), objective )
			this.goal_refresh( this.goal_refresh() + 1 )
		}

		override goal_clear( next?: Event ) {
			if( next === undefined ) return null
			$mol_wire_async( this ).goal_clear_later( this.session_id(), this.goal_state() === 'active' ).catch( $mol_fail_log )
			return next
		}

		goal_clear_later( id: string, pause: boolean ) {
			if( pause ) this.agent_goal_pause( id )
			else {
				this.agent_goal_clear( id )
				this.goal_draft( id, '' )
			}
			this.busy_local( false )
			this.thread_refresh( this.thread_refresh() + 1 )
			this.goal_refresh( this.goal_refresh() + 1 )
		}

		composer_content() {
			return [
				...( this.activity_error() ? [ this.Activity_error() ] : [] ),
				this.Composer_attachments(),
				this.Composer_row(),
			]
		}

		attachments_empty() {
			return !this.attachments().length
		}

		composer_row_content() {
			return [ this.Prompt(), this.Composer_actions() ]
		}

		composer_action_rows() {
			return [ this.Send(), ...( this.busy() ? [ this.Stop() ] : [] ) ]
		}

		image_files( files: FileList | readonly File[] | null | undefined ) {
			const supported = new Set([ 'image/png', 'image/jpeg', 'image/gif', 'image/webp' ])
			return [ ... ( files ?? [] ) ].filter( file => supported.has( file.type.toLowerCase() ) && file.size <= 10 * 1024 * 1024 )
		}

		attachments_add( files: readonly File[] ) {
			const current = this.attachments()
			const images = this.image_files( files ).slice( 0, Math.max( 0, 10 - current.length ) )
			if( !images.length ) return current
			const added = images.map( ( file, index )=> ({
				id: `${ Date.now() }-${ current.length + index }-${ file.name }-${ file.size }-${ file.lastModified }`,
				name: file.name || `Изображение ${ current.length + index + 1 }`,
				type: file.type,
				data: URL.createObjectURL( file ),
				file,
			}) )
			return this.attachments([ ... current, ... added ])
		}

		override attachment_paste( next?: ClipboardEvent ) {
			if( !next ) return null
			const files = this.image_files( next.clipboardData?.files )
			if( !files.length ) return next
			next.preventDefault()
			this.attachments_add( files )
			return next
		}

		attachment_drag_files( event: DragEvent ) {
			return Array.from( event.dataTransfer?.types ?? [] ).includes( 'Files' )
		}

		override attachment_drag_enter( next?: DragEvent ) {
			if( !next || !this.attachment_drag_files( next ) ) return next ?? null
			next.preventDefault()
			this.attachment_drag_depth( this.attachment_drag_depth() + 1 )
			this.attachment_drag_active( true )
			return next
		}

		override attachment_drag_over( next?: DragEvent ) {
			if( !next || !this.attachment_drag_files( next ) ) return next ?? null
			next.preventDefault()
			if( next.dataTransfer ) next.dataTransfer.dropEffect = 'copy'
			return next
		}

		override attachment_drag_leave( next?: DragEvent ) {
			if( !next || !this.attachment_drag_active() ) return next ?? null
			const depth = Math.max( 0, this.attachment_drag_depth() - 1 )
			this.attachment_drag_depth( depth )
			if( !depth ) this.attachment_drag_active( false )
			return next
		}

		override attachment_drop( next?: DragEvent ) {
			if( !next ) return null
			const files = this.image_files( next.dataTransfer?.files )
			if( !files.length ) return next
			next.preventDefault()
			this.attachment_drag_depth( 0 )
			this.attachment_drag_active( false )
			this.attachments_add( files )
			return next
		}

		attachment_rows() {
			return this.attachments().map( attachment => this.Attachment( attachment.id ) )
		}

		attachment( id: string ) {
			return this.attachments().find( attachment => attachment.id === id )
		}

		attachment_uri( id: string ) {
			return this.attachment( id )?.data ?? ''
		}

		attachment_name( id: string ) {
			return this.attachment( id )?.name ?? ''
		}

		override attachment_remove( id: string, next?: Event ) {
			if( next === undefined ) return null
			const attachment = this.attachment( id )
			if( attachment?.file ) URL.revokeObjectURL( attachment.data )
			this.attachments( this.attachments().filter( attachment => attachment.id !== id ) )
			return next
		}

		attachments_payload( source: readonly $shm_agent_attachment[] = this.attachments() ) {
			return source.map( ({ file, ...attachment })=> file ? ({
				... attachment,
				data: `data:${ file.type };base64,${ $mol_base64_encode( new Uint8Array( $mol_wire_sync( file ).arrayBuffer() ) ) }`,
			}) : attachment )
		}

		attachments_clear() {
			for( const attachment of this.attachments() ) if( attachment.file ) URL.revokeObjectURL( attachment.data )
			this.attachments( [] )
		}

		attachment_urls_revoke( source: readonly $shm_agent_attachment[] ) {
			for( const attachment of source ) if( attachment.file ) URL.revokeObjectURL( attachment.data )
		}

		fork_enabled() {
			return Boolean( this.session_id() && !this.forking() && !this.fork_pending() )
		}

		override fork( next?: Event ) {
			if( next === undefined ) return null
			if( !this.fork_enabled() ) return next
			const source = this.session_id()
			// Forking must not wait for a thread refresh. The server already owns the
			// canonical history; this snapshot is only used to paint the temporary chat.
			const source_session = this.$.$mol_error_fence(
				()=> this.session_by_id( source ),
				()=> null,
				()=> null,
			)
			const source_thread = this.$.$mol_error_fence(
				()=> this.thread_data(),
				()=> null,
				()=> null,
			)
			const source_title = this.$.$mol_error_fence(
				()=> this.session_title( source ),
				()=> `Сессия ${ source.slice( 0, 8 ) }`,
				()=> `Сессия ${ source.slice( 0, 8 ) }`,
			)
			const known_sessions = this.$.$mol_error_fence(
				()=> new Set( this.sessions_data().map( session => session.id ) ),
				()=> new Set< string >(),
				()=> new Set< string >(),
			)
			const temporary = `pending-fork:${ source }:${ $mol_guid() }`
			const optimistic_session: $shm_agent_session = {
				... source_session,
				id: temporary,
				name: `${ source_title } — форк`,
				createdAt: Date.now() / 1000,
				updatedAt: Date.now() / 1000,
				forkedFromId: source,
				status: { type: 'creating' },
			}
			this.pending_fork_sources.set( temporary, source )
			this.pending_fork_known_sessions.set( temporary, known_sessions )
			this.pending_fork_threads.set( temporary, {
				...( source_thread ?? { thread: source_session ?? { id: source } } ),
				thread: {
					...( source_thread?.thread ?? source_session ?? {} ),
					... optimistic_session,
				},
				activeTurnId: null,
				queue: [],
			} )
			this.session_upsert( optimistic_session )
			this.session_id( temporary )
			this.busy_local( true )
			this.session_bring()
			this.forking( true )
			$mol_wire_async( this ).fork_later( source, temporary ).catch( error => {
				const pending = this.pending_fork_queue.get( temporary ) ?? []
				const draft = this.session_prompt( temporary )
				const attachments = [ ... this.session_attachments( temporary ) ]
				if( this.session_id() === temporary ) this.session_id( source )
				this.session_remove( temporary )
				this.pending_fork_sources.delete( temporary )
				this.pending_fork_known_sessions.delete( temporary )
				this.pending_fork_threads.delete( temporary )
				this.pending_fork_queue.delete( temporary )
				this.session_busy_local( temporary, false )
				this.session_activity_error( source, `Не удалось создать форк: ${ this.send_error_message( error ).replace( /^Не отправлено:\s*/, '' ) }` )
				if( draft ) this.session_prompt( source, draft )
				if( attachments.length ) this.session_attachments( source, attachments )
				this.session_prompt( temporary, '' )
				this.session_attachments( temporary, [] )
				if( pending.length && !this.prompt().trim() ) {
					this.prompt( pending.map( item => item.text ).filter( Boolean ).join( '\n\n' ) )
					this.attachments( pending.flatMap( item => item.attachments ) )
				}
				this.forking( false )
				$mol_fail_log( error )
			} )
			return next
		}

		fork_later( source: string, temporary: string ) {
			const result = this.agent_session_fork( source )
			this.pending_fork_adopt( temporary, result.thread )
			return result.thread.id
		}

		pending_fork_adopt( temporary: string, target_session: $shm_agent_session ) {
			const target = target_session.id
			const active = this.pending_fork_sources.has( temporary )
				|| this.pending_fork_threads.has( temporary )
				|| this.pending_fork_queue.has( temporary )
			if( !active && this.session_id() !== temporary ) return target
			const pending = [ ...( this.pending_fork_queue.get( temporary ) ?? [] ) ]
			const optimistic = this.live_items( temporary )
			const draft = this.session_prompt( temporary )
			const attachments = [ ... this.session_attachments( temporary ) ]
			this.session_remove( temporary )
			this.session_upsert( target_session )
			this.live_items( target, { ... this.live_items( target ), ... optimistic } )
			this.live_items( temporary, {} )
			if( draft ) this.session_prompt( target, draft )
			if( attachments.length ) this.session_attachments( target, attachments )
			this.session_prompt( temporary, '' )
			this.session_attachments( temporary, [] )
			this.pending_fork_sources.delete( temporary )
			this.pending_fork_known_sessions.delete( temporary )
			this.pending_fork_threads.delete( temporary )
			this.pending_fork_queue.delete( temporary )
			if( this.session_id() === temporary ) this.session_id( target )
			this.forking( false )
			this.session_busy_local( temporary, false )
			this.session_busy_local( target, Boolean( pending.length ) )
			this.session_bring()
			this.messages_to_bottom_force( target )
			if( pending.length ) $mol_wire_async( this ).pending_fork_drain( target, pending ).catch( error => {
				this.session_busy_local( target, false )
				this.session_activity_error( target, this.send_error_message( error ) )
				$mol_fail_log( error )
			} )
			return target
		}

		pending_fork_drain( target: string, pending: readonly {
			text: string
			optimistic: string
			attachments: readonly $shm_agent_attachment[]
			mode: 'start' | 'queue' | 'steer'
		}[] ) {
			for( const [ index, item ] of pending.entries() ) {
				const payload = this.attachments_payload( item.attachments )
				this.optimistic_message_images_update( target, item.optimistic, payload.map( attachment => attachment.data ) )
				const delivery = index ? 'queue' : 'start'
				const sent = this.agent_message_send(
					target,
					item.text,
					payload,
					delivery,
					item.optimistic.replace( /^optimistic:/, '' ),
				)
				const turn = String( sent?.turn?.id || sent?.turnId || '' )
				if( turn ) this.optimistic_message_turn_bind( target, item.optimistic, turn )
				if( sent?.queued ) this.optimistic_message_state( target, item.optimistic, 'В очереди', {
					queued: true,
					queueId: sent.message?.id,
					deliveryMode: 'queue',
				} )
				else this.optimistic_message_state( target, item.optimistic, '', { queued: false, deliveryMode: delivery } )
				this.attachment_urls_revoke( item.attachments )
			}
			this.thread_refresh( this.thread_refresh() + 1 )
			return true
		}

		busy() {
			return this.session_busy( this.session_id() )
		}

		override send( next?: Event ) {
			if( next === undefined ) return null
			this.message_submit( this.busy() ? 'queue' : 'start' )
			return next
		}

		send_hint() {
			return this.busy() ? 'Добавить в очередь' : 'Отправить'
		}

		queue( next?: Event ) {
			if( next === undefined ) return null
			this.message_submit( 'queue' )
			return next
		}

		steer( next?: Event ) {
			if( next === undefined ) return null
			this.message_submit( this.busy() ? 'steer' : 'start' )
			return next
		}

		override stop( next?: Event ) {
			if( next === undefined ) return null
			$mol_wire_async( this ).stop_later().catch( $mol_fail_log )
			return next
		}

		stop_enabled() {
			return this.busy()
		}

		message_submit( mode: 'start' | 'queue' | 'steer' ) {
			const text = this.prompt().trim()
			const attachments = [ ... this.attachments() ]
			if( !text && !attachments.length ) return false
			const thread = this.session_id()
			const auto_title = !thread || this.session_auto_title_needed( thread )
			const optimistic = this.optimistic_message_id()
			if( thread ) {
				this.optimistic_message_set( thread, optimistic, text, attachments.map( attachment => attachment.data ) )
				this.optimistic_message_state( thread, optimistic, '', { deliveryMode: mode } )
				this.messages_to_bottom_force( thread )
			}
			this.prompt( '' )
			this.attachments( [] )
			if( thread && mode === 'start' && !this.fork_pending( thread ) && !this.create_pending( thread ) ) {
				this.session_busy_starting( thread, true )
			} else {
				this.busy_local( true )
			}
			this.activity_error( '' )
			if( this.fork_pending( thread ) || this.create_pending( thread ) ) {
				this.optimistic_message_state( thread, optimistic, 'В очереди', {
					queued: true,
					deliveryMode: 'queue',
				} )
				const queue = this.create_pending( thread ) ? this.pending_create_queue : this.pending_fork_queue
				queue.set( thread, [
					...( queue.get( thread ) ?? [] ),
					{ text, optimistic, attachments, mode: 'queue' },
				] )
				return true
			}
			const sending = $mol_wire_async( this ).send_later( text, optimistic, attachments, mode )
			sending.catch( error => {
				const target = thread || this.session_id()
				if( target ) this.optimistic_message_fail( target, optimistic, error )
				this.attachments([ ... attachments, ... this.attachments() ])
				if( mode === 'start' && target ) {
					this.session_busy_confirm_idle_now( target )
				}
				$mol_fail_log( error )
			} )
			if( auto_title ) sending
				.then( id => $mol_wire_async( this ).session_auto_title( id, text ) )
				.catch( ()=> null )
			return true
		}

		send_later(
			text: string,
			optimistic = this.optimistic_message_id(),
			attachments: readonly $shm_agent_attachment[] = [],
			mode: 'start' | 'queue' | 'steer' = 'start',
		) {
			const payload = this.attachments_payload( attachments )
			const id = this.session_id() || $mol_wire_sync( this ).session_create_later( this.session_title_next() )
			if( mode === 'start' ) {
				this.session_busy_starting( id, true )
			}
			$mol_wire_sync( this ).optimistic_message_set( id, optimistic, text, payload.map( attachment => attachment.data ) )
			this.optimistic_message_images_update( id, optimistic, payload.map( attachment => attachment.data ) )
			this.optimistic_message_state( id, optimistic, '', { deliveryMode: mode } )
			const result = this.agent_message_send( id, text, payload, mode, optimistic.replace( /^optimistic:/, '' ) )
			const turn = String( result?.turn?.id || result?.turnId || '' )
			if( turn ) this.optimistic_message_turn_bind( id, optimistic, turn )
			if( mode === 'start' ) this.session_busy_confirm_active( id )
			if( result?.queued ) this.optimistic_message_state( id, optimistic, 'В очереди', { queueId: result.message?.id } )
			if( result?.steered ) this.optimistic_message_state( id, optimistic, 'Направлено в текущий ответ' )
			this.attachment_urls_revoke( attachments )
			this.session_thread_refresh( id, this.session_thread_refresh( id ) + 1 )
			return id
		}

		stop_later() {
			const id = this.session_id()
			this.agent_session_stop( id )
			this.session_busy_confirm_idle_now( id )
			this.session_thread_refresh( id, this.session_thread_refresh( id ) + 1 )
		}

	}

}
